import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LuAward,
  LuBuilding2,
  LuClipboardList,
  LuGlobe,
  LuLayoutGrid,
  LuMapPin,
  LuShieldCheck,
  LuTruck,
} from "react-icons/lu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  LOGISTICS_CORE_SPECIALIZATION,
  LOGISTICS_INDUSTRIES_SERVED,
  LOGISTICS_SPECIAL_CARGO,
  LOGISTICS_TRANSPORT_MODES,
} from "../../constants/logisticsProfileFormOptions";
import "../../assets/styles/logistics_provider_profile.scss";

const SECTIONS = [
  { key: "basic", label: "Basic Information" },
  { key: "transport", label: "Transport Modes" },
  { key: "contact", label: "Contact Details" },
  { key: "capabilities", label: "Capabilities" },
  { key: "fleet", label: "Fleet & Assets" },
  { key: "coverage", label: "Coverage & Routes" },
  { key: "certifications", label: "Certifications" },
  { key: "commercial", label: "Commercial Terms & Pricing" },
];

/** Five onboarding tabs — each maps to one or more existing SECTIONS (content unchanged). */
const PROFILE_TABS = [
  {
    id: "company",
    label: "Company Profile",
    TabIcon: LuBuilding2,
    sectionKeys: ["basic"],
  },
  {
    id: "infrastructure",
    label: "Infrastructure & Fleet",
    TabIcon: LuTruck,
    sectionKeys: ["fleet"],
  },
  {
    id: "services",
    label: "Services & Coverage",
    TabIcon: LuMapPin,
    sectionKeys: ["coverage", "commercial"],
  },
  {
    id: "verification",
    label: "Verification & Performance",
    TabIcon: LuShieldCheck,
    sectionKeys: ["certifications", "capabilities"],
  },
  {
    id: "catalogue",
    label: "Service Catalogue",
    TabIcon: LuLayoutGrid,
    sectionKeys: ["transport"],
  },
];

/** Wizard-only sections (not on tab bar) still highlight the nearest tab. */
const SECTION_TAB_FALLBACK = {
  contact: "company",
};

/** Hero banner — matches edit-profile mockup (display only). */
const LP_HERO_BENEFITS = [
  {
    n: 1,
    title: "Nationwide Logistics Network",
    text: "Deliver across India and worldwide",
    Icon: LuGlobe,
  },
  {
    n: 2,
    title: "Verified Logistics Provider",
    text: "Trusted & verified service partner",
    Icon: LuShieldCheck,
  },
  {
    n: 3,
    title: "Premium RFQ Access",
    text: "Get premium RFQs before others",
    Icon: LuClipboardList,
  },
  {
    n: 4,
    title: "Exhibition Logistics",
    text: "Specialized in event & exhibition logistics",
    Icon: LuTruck,
  },
  {
    n: 5,
    title: "Business Relocation",
    text: "End-to-end relocation solutions",
    Icon: LuBuilding2,
  },
  {
    n: 6,
    title: "Featured Logistics Partner",
    text: "Get featured and grow your business",
    Icon: LuAward,
  },
];

const COMPANY_TYPE_OPTIONS = [
  { value: "", label: "Select company type" },
  { value: "Asset Owner", label: "Asset Owner" },
  { value: "Aggregator", label: "Aggregator" },
  { value: "Broker", label: "Broker" },
  { value: "Fleet Owner", label: "Fleet Owner" },
  { value: "NVOCC", label: "NVOCC" },
  { value: "Freight Forwarder", label: "Freight Forwarder" },
  { value: "Carrier", label: "Carrier" },
  { value: "Multimodal Operator", label: "Multimodal Operator" },
];

/** QA profile–style mapped options (no empty value — placeholder on Select) */
const COMPANY_TYPE_SELECT_OPTIONS = COMPANY_TYPE_OPTIONS.filter((o) => o.value !== "");

function trimStr(v) {
  return String(v ?? "").trim();
}

/** Resolve company profile object from GET /company/my-profile (handles nested `data`). */
function tradeCompanyProfileFromResponse(resp) {
  if (!resp || typeof resp !== "object") return null;
  const pick = (o) =>
    o &&
    typeof o === "object" &&
    (o.company_id != null ||
      o.id != null ||
      trimStr(o.company_name) ||
      trimStr(o.companyName) ||
      o.user_login_id != null)
      ? o
      : null;
  return pick(resp) || pick(resp.data) || pick(resp.data?.data) || null;
}

/** Map snake_case basic_information from API/DB into camelCase fields the form binds to. */
function normalizeLogisticsBasicInformation(bi) {
  if (!bi || typeof bi !== "object") return bi;
  const out = { ...bi };
  const name =
    trimStr(out.companyName) || trimStr(out.company_name) || trimStr(out.company_name_display);
  if (name) out.companyName = name;
  if (!trimStr(out.aboutCompany) && trimStr(out.about_company)) out.aboutCompany = trimStr(out.about_company);
  if (!trimStr(out.headquartersLocation) && trimStr(out.headquarters_location)) {
    out.headquartersLocation = trimStr(out.headquarters_location);
  }
  if (!trimStr(out.companyLogo) && trimStr(out.company_logo)) out.companyLogo = trimStr(out.company_logo);
  if (!trimStr(out.companyType) && trimStr(out.company_type)) out.companyType = trimStr(out.company_type);
  const yib = out.yearsInBusiness ?? out.years_in_business;
  if ((out.yearsInBusiness === "" || out.yearsInBusiness == null) && yib !== "" && yib != null) {
    const n = Number(yib);
    if (!Number.isNaN(n)) out.yearsInBusiness = String(yib);
  }
  if (!trimStr(out.operatingModel) && trimStr(out.operating_model)) {
    out.operatingModel = trimStr(out.operating_model);
  }
  return out;
}

/** Map snake_case / alternate keys from API/DB into contact fields the form uses. */
function normalizeLogisticsContactDetails(cd) {
  if (!cd || typeof cd !== "object") return cd;
  const out = { ...cd };
  const email =
    trimStr(out.primaryEmail) ||
    trimStr(out.primary_email) ||
    trimStr(out.email) ||
    trimStr(out.contact_email);
  if (email) out.primaryEmail = email;
  const phone =
    trimStr(out.primaryPhone) ||
    trimStr(out.primary_phone) ||
    trimStr(out.phone) ||
    trimStr(out.contact_number) ||
    trimStr(out.mobile) ||
    trimStr(out.mobile_number);
  if (phone) out.primaryPhone = phone;
  const nm =
    trimStr(out.primaryName) ||
    trimStr(out.primary_name) ||
    trimStr(out.contact_name) ||
    trimStr(out.name);
  if (nm) out.primaryName = nm;
  const des =
    trimStr(out.primaryDesignation) ||
    trimStr(out.primary_designation) ||
    trimStr(out.designation) ||
    trimStr(out.role);
  if (des) out.primaryDesignation = des;
  return out;
}

/**
 * Fill empty logistics fields from trade company profile + stored auth user (signup / login).
 * Does not overwrite existing logistics-only data.
 */
function applyTradeCompanyPrefill(formState, profile, authUser) {
  const au = authUser && typeof authUser === "object" ? authUser : null;
  if (!profile && !au) return formState;
  const bi = { ...formState.basic_information };
  const cd = { ...formState.contact_details };
  const regs = { ...(formState.certifications?.registrations || {}) };

  if (profile) {
    if (!trimStr(bi.companyName)) {
      bi.companyName =
        trimStr(profile.company_name) ||
        trimStr(profile.companyName) ||
        trimStr(bi.company_name) ||
        bi.companyName;
    }
    if (!trimStr(bi.companyLogo) && profile.media?.company_logo) bi.companyLogo = profile.media.company_logo;
    if (!trimStr(bi.aboutCompany) && profile.company_description) bi.aboutCompany = profile.company_description;

    if (!trimStr(bi.headquartersLocation)) {
      const addr = profile.address || {};
      const parts = [addr.city, addr.state, addr.country].map(trimStr).filter(Boolean);
      if (parts.length) bi.headquartersLocation = parts.join(", ");
    }

    if (!trimStr(bi.yearsInBusiness) && profile.establishment_year != null && profile.establishment_year !== "") {
      const y = parseInt(String(profile.establishment_year).trim(), 10);
      const currentYear = new Date().getFullYear();
      if (!Number.isNaN(y) && y > 1800 && y <= currentYear) {
        bi.yearsInBusiness = String(currentYear - y);
      }
    }

    const pcd = profile.contact_details || {};
    if (!trimStr(cd.primaryName)) {
      cd.primaryName =
        trimStr(pcd.contact_name) ||
        trimStr(profile.user_name) ||
        trimStr(profile.name) ||
        trimStr(au?.name) ||
        cd.primaryName;
    }
    if (!trimStr(cd.primaryEmail)) {
      cd.primaryEmail =
        trimStr(pcd.contact_email) ||
        trimStr(pcd.alternate_email) ||
        trimStr(profile.user_email) ||
        trimStr(profile.email) ||
        trimStr(au?.email) ||
        cd.primaryEmail;
    }
    if (!trimStr(cd.primaryPhone)) {
      cd.primaryPhone =
        trimStr(pcd.contact_number) ||
        trimStr(pcd.alternate_contact_number) ||
        trimStr(profile.user_phone) ||
        trimStr(profile.phone) ||
        trimStr(au?.phone) ||
        cd.primaryPhone;
    }
    if (!trimStr(cd.primaryDesignation)) {
      cd.primaryDesignation = trimStr(profile.designation) || cd.primaryDesignation;
    }

    if (!trimStr(regs.gstin) && profile.tax_number) regs.gstin = profile.tax_number;
    if (!trimStr(regs.iec) && profile.iec_number) regs.iec = profile.iec_number;
  }

  if (au) {
    if (!trimStr(bi.companyName)) bi.companyName = trimStr(au.company_name) || bi.companyName;
    if (!trimStr(cd.primaryName)) cd.primaryName = trimStr(au.name) || cd.primaryName;
    if (!trimStr(cd.primaryEmail)) cd.primaryEmail = trimStr(au.email) || cd.primaryEmail;
    if (!trimStr(cd.primaryPhone)) cd.primaryPhone = trimStr(au.phone) || cd.primaryPhone;
  }

  return {
    ...formState,
    basic_information: normalizeLogisticsBasicInformation(bi),
    contact_details: normalizeLogisticsContactDetails(cd),
    certifications: {
      ...formState.certifications,
      registrations: regs,
    },
  };
}

export default function LogisticsProviderProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const onboarding = new URLSearchParams(location.search).get("onboarding");
  const invitePopup = new URLSearchParams(location.search).get("invitePopup") === "1";

  const [activeSection, setActiveSection] = useState("basic");
  const [activeTabId, setActiveTabId] = useState("company");
  const [mergedTabView, setMergedTabView] = useState(true);
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [coverageInput, setCoverageInput] = useState("");
  const [airportInput, setAirportInput] = useState("");
  const [certModalOpen, setCertModalOpen] = useState(false);
  const [certDraft, setCertDraft] = useState({
    name: "",
    issuer: "",
    issuedDate: "",
    validity: "",
    attachmentUrl: "",
  });
  const [officeDraft, setOfficeDraft] = useState({ name: "", address: "", phone: "" });
  const [portDraft, setPortDraft] = useState({ name: "", portType: "", traffic: "" });
  const [corridorDraft, setCorridorDraft] = useState({
    name: "",
    modes: [],
    frequency: "",
    traffic: "",
  });
  const [languageOptions, setLanguageOptions] = useState([]);
  const [paymentTermOptions, setPaymentTermOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);

  const logoInputRef = useRef(null);
  const certFileRef = useRef(null);
  const lpCardRef = useRef(null);

  const [form, setForm] = useState({
    basic_information: {
      companyName: "",
      companyLogo: "",
      companyType: "",
      yearsInBusiness: "",
      headquartersLocation: "",
      aboutCompany: "",
      operatingModel: "",
      // View page quick-stats / hero
      responseTime: "", // e.g. "< 2 hours"
      averageRating: "", // e.g. 4.7
      reviewsCount: "", // e.g. 348
      completedShipments: "", // e.g. 12500
      repeatCustomersPercent: "", // e.g. 78
      verifiedProvider: false,
      activeOnPlatform: false, // "Active on Troconet"
    },
    transport_modes: {
      modes: [], // ['road','ocean','rail','air','multimodal']
      preferredShipmentTypes: [], // ['FTL','LTL','PTL','FCL','LCL','Express Air Cargo']
    },
    contact_details: {
      primaryName: "",
      primaryDesignation: "",
      primaryEmail: "",
      primaryPhone: "",
      languagesSupported: [], // ['English', ...]
      customerSupport247: false,
      officeLocations: [], // [{ name, address, phone }]
      businessHours: { monFri: "", saturday: "", sunday: "" },
      communicationChannels: { callUs: false, email: true, liveChat: false, scheduleCall: false },
      availabilitySummary: "",
      timezone: "",
    },
    capabilities: {
      coreSpecialization: [],
      industriesServed: [],
      specialCargoCapabilities: [],
      serviceHighlights: [], // ["Real-time Tracking", ...]
      additionalServiceCapabilities: [], // ["Warehousing", "Customs & Compliance", "Insurance & Risk"]
      operationalByMode: {
        road: { serviceTypes: [], coverage: "", specialCargo: "", tracking: "", notes: "" },
        ocean: { containerTypes: [], serviceModel: "", specialCargo: "", customerSupport: "", notes: "" },
        air: { serviceTypes: [], cargoTypes: "", airlines: "", temperatureControl: "", notes: "" },
        rail: { serviceTypes: [], coverage: "", specialCargo: "", tracking: "", notes: "" },
        multimodal: { serviceTypes: [], coverage: "", specialCargo: "", tracking: "", notes: "" },
      },
      commercialTerms: {
        pricingModelsOffered: [],
        paymentTerms: [],
        creditPeriodDays: "",
        creditPeriodLabel: "",
        currenciesAccepted: [],
        commercialSupport: [],
        minimumRequirements: [],
      },
      performance: {
        onTimeDeliveryPercent: "",
        onTimeDeliveryTargetPercent: "",
        onTimeDeliveryBadge: "",
        onTimeIndustryAvgPercent: "",
        avgResponseHours: "",
        quoteResponseTargetHours: "",
        quoteResponseBadge: "",
        quoteResponseIndustryAvgHours: "",
        customerRating: "",
        customerRatingBadge: "",
        customerReviewsCount: "",
        customerRecommendedPercent: "",
        repeatCustomersPercent: "",
        repeatCustomersBadge: "",
        repeatCustomersIndustryAvgPercent: "",
        bookingConfirmationHours: "",
        shipmentSuccessPercent: "",
        claimsRatioPercent: "",
        industryTargets: {
          documentationAccuracyPercent: "",
          realTimeTrackingVisibilityPercent: "",
          customsClearanceSuccessPercent: "",
          customerSupportResponsePercent: "",
          cancellationRatePercent: "",
          avgDelayHours: "",
        },
        serviceLevels: {
          documentationAccuracyPercent: "",
          documentationAccuracyTargetPercent: "",
          realTimeTrackingVisibilityPercent: "",
          realTimeTrackingTargetPercent: "",
          customsClearanceSuccessPercent: "",
          customsClearanceTargetPercent: "",
          customerSupportResponsePercent: "",
          customerSupportTargetPercent: "",
          cancellationRatePercent: "",
          cancellationTargetPercent: "",
          avgDelayHours: "",
          avgDelayTargetHours: "",
        },
      },
      customerReviews: {
        overallRating: "",
        totalReviewsCount: "",
        ratingDistribution: { 5: "", 4: "", 3: "", 2: "", 1: "" },
        reviews: [],
      },
    },
    fleet_assets: {
      warehousesStorage: {
        totalWarehouses: "",
        totalAreaSqFt: "",
        coldStorageUnits: "",
        bondedWarehouses: "",
      },
      overview: {
        totalTrucks: "",
        containersTotal: "",
        railWagonsTotal: "",
        warehousesTotal: "",
      },
      roadFleetBreakdown: {
        containerTrucks: "",
        openBodyTrucks: "",
        refrigeratedTrucks: "",
        tankers: "",
        flatbedTrailers: "",
        lowBedTrailers: "",
        multiAxleVehicles: "",
        lightCommercial: "",
      },
      technologyTracking: {
        gpsFleetTracking: false,
        iotSensors: false,
        fleetManagementSystem: false,
      },
    },
    coverage_routes: {
      geographicCoverage: [], // ['India', 'UAE', ...]
      majorTradeLanes: [], // ['India-UAE', ...]
      portsTerminals: [], // ["JNPT - Mumbai", ...]
      airportNetwork: [], // ["Mumbai (BOM)", ...]
      corridors: [], // [{ name, modes: [], frequency, priority }]
    },
    certifications: {
      registrations: {
        cinNumber: "",
        gstin: "",
        pan: "",
        iec: "",
        msme: "",
        tan: "",
      },
      industryCertifications: [], // [{ name, issuer, issuedDate, validity, attachmentUrl }]
      insuranceSafety: {
        cargoInsurance: { provider: "", coverage: "", validUntil: "" },
        safetyCompliance: [], // ["Driver Training Program", ...]
      },
      esg: {
        environmental: [],
        social: [],
        governance: [],
      },
    },
  });

  const sectionMeta = useMemo(
    () => SECTIONS.find((s) => s.key === activeSection) || SECTIONS[0],
    [activeSection]
  );

  const sectionIndex = useMemo(
    () => Math.max(0, SECTIONS.findIndex((s) => s.key === activeSection)),
    [activeSection]
  );


  const pageHeading = companyId ? "Edit Logistics Provider Profile" : "Create Logistics Provider Profile";
  const pageLead =
    "Manage your logistics company details, services, fleet, coverage and operational capabilities.";

  const scrollCardIntoView = () => {
    requestAnimationFrame(() => {
      lpCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const resolveTabIdForSection = (sectionKey) => {
    const tab = PROFILE_TABS.find((t) => t.sectionKeys.includes(sectionKey));
    if (tab) return tab.id;
    return SECTION_TAB_FALLBACK[sectionKey] ?? PROFILE_TABS[0].id;
  };

  const showSection = useCallback(
    (sectionKey) => {
      if (mergedTabView) {
        const tab = PROFILE_TABS.find((t) => t.id === activeTabId);
        return tab?.sectionKeys.includes(sectionKey) ?? false;
      }
      return activeSection === sectionKey;
    },
    [activeTabId, activeSection, mergedTabView]
  );

  const selectTab = (tab) => {
    setActiveTabId(tab.id);
    setActiveSection(tab.sectionKeys[0]);
    setMergedTabView(true);
    scrollCardIntoView();
  };

  const goToSection = (sectionKey) => {
    setActiveSection(sectionKey);
    setActiveTabId(resolveTabIdForSection(sectionKey));
    setMergedTabView(false);
    scrollCardIntoView();
  };

  const goPrevSection = () => {
    if (sectionIndex <= 0) return;
    goToSection(SECTIONS[sectionIndex - 1].key);
  };

  const goNextSection = () => {
    if (sectionIndex >= SECTIONS.length - 1) return;
    goToSection(SECTIONS[sectionIndex + 1].key);
  };

  const setNested = (rootKey, name, value) => {
    setForm((p) => ({
      ...p,
      [rootKey]: {
        ...(p[rootKey] || {}),
        [name]: value,
      },
    }));
  };

  const toggleNestedArrayValue = (rootKey, name, value) => {
    setForm((p) => {
      const cur = Array.isArray(p?.[rootKey]?.[name]) ? p[rootKey][name] : [];
      const exists = cur.includes(value);
      const next = exists ? cur.filter((v) => v !== value) : [...cur, value];
      return {
        ...p,
        [rootKey]: {
          ...(p[rootKey] || {}),
          [name]: next,
        },
      };
    });
  };

  const languageSelectOptions = useMemo(
    () =>
      languageOptions.map((o) => {
        const name = String(o.label);
        const flag = o.flag ? String(o.flag).trim() : "";
        return {
          value: name,
          label: flag ? `${flag} ${name}` : name,
        };
      }),
    [languageOptions]
  );

  const languagesSupportedSelectValue = useMemo(() => {
    const selected = form.contact_details?.languagesSupported || [];
    return languageSelectOptions.filter((opt) => selected.includes(opt.value));
  }, [languageSelectOptions, form.contact_details?.languagesSupported]);

  const companyTypeSelectValue = useMemo(() => {
    const v = String(form.basic_information?.companyType || "").trim();
    return COMPANY_TYPE_SELECT_OPTIONS.find((o) => o.value === v) || null;
  }, [form.basic_information?.companyType]);

  const modeServiceTypeOptions = useMemo(
    () => ({
      road: ["FTL", "LTL", "PTL"],
      air: ["Express", "Standard"],
      rail: ["Container", "Bulk", "Temperature Controlled", "Hazmat"],
      multimodal: ["Door-to-Door", "Port-to-Door", "Door-to-Port", "Cross-border"],
    }),
    []
  );

  const modeServiceTypeSelectOptions = useMemo(() => {
    const build = (arr) => (arr || []).map((x) => ({ value: x, label: x }));
    return {
      road: build(modeServiceTypeOptions.road),
      air: build(modeServiceTypeOptions.air),
      rail: build(modeServiceTypeOptions.rail),
      multimodal: build(modeServiceTypeOptions.multimodal),
    };
  }, [modeServiceTypeOptions]);

  const modeServiceTypesSelectValue = useCallback(
    (modeKey) => {
      const selected = form.capabilities?.operationalByMode?.[modeKey]?.serviceTypes || [];
      const opts = modeServiceTypeSelectOptions[modeKey] || [];
      return opts.filter((o) => Array.isArray(selected) && selected.includes(o.value));
    },
    [form.capabilities?.operationalByMode, modeServiceTypeSelectOptions]
  );

  const oceanContainerTypeSelectOptions = useMemo(
    () => ["20ft", "40ft", "40HC", "Reefer"].map((x) => ({ value: x, label: x })),
    []
  );

  const oceanContainerTypesSelectValue = useMemo(() => {
    const selected = form.capabilities?.operationalByMode?.ocean?.containerTypes || [];
    return oceanContainerTypeSelectOptions.filter((o) => Array.isArray(selected) && selected.includes(o.value));
  }, [form.capabilities?.operationalByMode?.ocean?.containerTypes, oceanContainerTypeSelectOptions]);

  const preferredShipmentTypeOptions = useMemo(() => {
    const selectedModes = Array.isArray(form.transport_modes?.modes) ? form.transport_modes.modes : [];
    const opts = [];
    const push = (x) => {
      if (!x || opts.includes(x)) return;
      opts.push(x);
    };

    if (selectedModes.includes("road")) {
      ["FTL", "LTL", "PTL"].forEach(push);
    }
    if (selectedModes.includes("ocean")) {
      ["FCL", "LCL"].forEach(push);
    }
    if (selectedModes.includes("air")) {
      ["Express Air Cargo"].forEach(push);
    }
    if (selectedModes.includes("rail")) {
      ["Rail Cargo"].forEach(push);
    }
    if (selectedModes.includes("multimodal")) {
      ["Door-to-Door"].forEach(push);
    }

    return opts;
  }, [form.transport_modes?.modes]);

  useEffect(() => {
    setForm((p) => {
      const cur = Array.isArray(p.transport_modes?.preferredShipmentTypes) ? p.transport_modes.preferredShipmentTypes : [];
      const next = cur.filter((x) => preferredShipmentTypeOptions.includes(x));
      if (next.length === cur.length) return p;
      return {
        ...p,
        transport_modes: {
          ...(p.transport_modes || {}),
          preferredShipmentTypes: next,
        },
      };
    });
  }, [preferredShipmentTypeOptions]);

  const paymentTermSelectOptions = useMemo(() => {
    const base = paymentTermOptions.map((o) => {
      const name = String(o.label ?? "").trim();
      return { value: name, label: name };
    });
    const saved = form.capabilities?.commercialTerms?.paymentTerms || [];
    const extras = saved
      .filter((s) => s && !base.some((opt) => opt.value === String(s).trim()))
      .map((s) => {
        const v = String(s).trim();
        return { value: v, label: v };
      });
    return [...base, ...extras];
  }, [paymentTermOptions, form.capabilities?.commercialTerms?.paymentTerms]);

  const paymentTermsSelectValue = useMemo(() => {
    const selected = form.capabilities?.commercialTerms?.paymentTerms || [];
    return paymentTermSelectOptions.filter((opt) => selected.includes(opt.value));
  }, [paymentTermSelectOptions, form.capabilities?.commercialTerms?.paymentTerms]);

  const currencySelectOptions = useMemo(() => {
    const base = currencyOptions.map((o) => {
      const code = o.code;
      const sym = o.symbol ? String(o.symbol).trim() : "";
      const label = sym ? `${code} (${sym})` : code;
      return { value: code, label };
    });
    const saved = form.capabilities?.commercialTerms?.currenciesAccepted || [];
    const extras = saved
      .filter((s) => s && !base.some((opt) => opt.value === String(s).trim()))
      .map((s) => {
        const v = String(s).trim();
        return { value: v, label: v };
      });
    return [...base, ...extras];
  }, [currencyOptions, form.capabilities?.commercialTerms?.currenciesAccepted]);

  const currenciesAcceptedSelectValue = useMemo(() => {
    const selected = form.capabilities?.commercialTerms?.currenciesAccepted || [];
    return currencySelectOptions.filter((opt) => selected.includes(opt.value));
  }, [currencySelectOptions, form.capabilities?.commercialTerms?.currenciesAccepted]);

  const loadCompanyId = async () => {
    try {
      const resp = await CommonService.get("/company/my-profile");
      const p = tradeCompanyProfileFromResponse(resp);
      const cid = p?.company_id || p?.id || null;
      if (cid) setCompanyId(cid);
      return cid;
    } catch (e) {
      return null;
    }
  };

  const loadExisting = async (opts = {}) => {
    const silent = !!opts.silent;
    if (!silent) setLoading(true);
    try {
      const companyResp = await CommonService.get("/company/my-profile");
      const profile = tradeCompanyProfileFromResponse(companyResp);
      const cid = profile?.company_id || profile?.id || null;
      if (cid) setCompanyId(cid);
      if (!cid) {
        // Trade profile not completed yet (required for FK to company_profile)
        return;
      }

      let logisticsData = null;
      try {
        const resp = await CommonService.get("/company/logistics/my-profile");
        if (resp?.success && resp?.data) logisticsData = resp.data;
      } catch (e) {
        // No logistics row yet — prefill from trade company below
      }

      setForm((p) => {
        const next = {
          ...p,
          basic_information: { ...p.basic_information },
          transport_modes: { ...p.transport_modes },
          contact_details: { ...p.contact_details },
          capabilities: {
            ...p.capabilities,
            commercialTerms: { ...(p.capabilities?.commercialTerms || {}) },
          },
          fleet_assets: {
            ...p.fleet_assets,
            warehousesStorage: { ...(p.fleet_assets?.warehousesStorage || {}) },
          },
          coverage_routes: { ...p.coverage_routes },
          certifications: {
            ...p.certifications,
            registrations: { ...(p.certifications?.registrations || {}) },
          },
        };

        if (logisticsData) {
          const d = logisticsData;
          const tm = d.transport_modes || {};
          const cd = d.contact_details || {};
          const cap = d.capabilities || {};
          const fa = d.fleet_assets || {};
          const legacyModes = [];
          if (tm.road) legacyModes.push("road");
          if (tm.sea) legacyModes.push("ocean");
          if (tm.rail) legacyModes.push("rail");
          if (tm.air) legacyModes.push("air");
          const normalizedTransportModes = {
            modes: Array.isArray(tm.modes) ? tm.modes : legacyModes,
            preferredShipmentTypes: Array.isArray(tm.preferredShipmentTypes)
              ? tm.preferredShipmentTypes
              : Array.isArray(tm.preferred_shipment_types)
                ? tm.preferred_shipment_types
                : [],
          };
          const bhFromApi = cd.businessHours || cd.business_hours || {};
          const chFromApi = cd.communicationChannels || cd.communication_channels || {};
          const officesFromApi = Array.isArray(cd.officeLocations)
            ? cd.officeLocations
            : Array.isArray(cd.office_locations)
              ? cd.office_locations
              : null;
          const normalizedContactDetails = {
            primaryName: cd.primaryName || cd.primary_name || "",
            primaryDesignation: cd.primaryDesignation || cd.role || cd.designation || cd.primary_designation || "",
            primaryEmail: cd.primaryEmail || cd.email || cd.primary_email || "",
            primaryPhone: cd.primaryPhone || cd.phone || cd.primary_phone || "",
            languagesSupported: Array.isArray(cd.languagesSupported)
              ? cd.languagesSupported
              : Array.isArray(cd.languages_supported)
                ? cd.languages_supported
                : [],
            customerSupport247:
              cd.customerSupport247 ??
              cd.customer_support_247 ??
              cd.customerSupport24x7 ??
              cd.customer_support_24x7 ??
              false,
            officeLocations: officesFromApi != null ? officesFromApi : next.contact_details.officeLocations || [],
            businessHours: {
              monFri: "",
              saturday: "",
              sunday: "",
              ...(next.contact_details?.businessHours || {}),
              ...bhFromApi,
            },
            communicationChannels: {
              callUs: false,
              email: true,
              liveChat: false,
              scheduleCall: false,
              ...(next.contact_details?.communicationChannels || {}),
              ...chFromApi,
            },
            availabilitySummary:
              cd.availabilitySummary ??
              cd.availability_summary ??
              cd.availabilityLine ??
              cd.availability_line ??
              next.contact_details?.availabilitySummary ??
              "",
            timezone: cd.timezone ?? cd.business_hours_timezone ?? next.contact_details?.timezone ?? "",
          };
          next.basic_information = normalizeLogisticsBasicInformation({
            ...next.basic_information,
            ...(d.basic_information || {}),
          });
          next.transport_modes = { ...next.transport_modes, ...normalizedTransportModes };
          next.contact_details = normalizeLogisticsContactDetails({
            ...next.contact_details,
            ...normalizedContactDetails,
          });
          next.capabilities = {
            ...next.capabilities,
            coreSpecialization: Array.isArray(cap.coreSpecialization)
              ? cap.coreSpecialization
              : Array.isArray(cap.core_specialization)
                ? cap.core_specialization
                : [],
            industriesServed: Array.isArray(cap.industriesServed)
              ? cap.industriesServed
              : Array.isArray(cap.industries_served)
                ? cap.industries_served
                : [],
            specialCargoCapabilities: Array.isArray(cap.specialCargoCapabilities)
              ? cap.specialCargoCapabilities
              : Array.isArray(cap.special_cargo_capabilities)
                ? cap.special_cargo_capabilities
                : Array.isArray(cap.specialCargo)
                  ? cap.specialCargo
                  : [],
            serviceHighlights: Array.isArray(cap.serviceHighlights)
              ? cap.serviceHighlights
              : Array.isArray(cap.service_highlights)
                ? cap.service_highlights
                : [],
            additionalServiceCapabilities: Array.isArray(cap.additionalServiceCapabilities)
              ? cap.additionalServiceCapabilities
              : Array.isArray(cap.additional_service_capabilities)
                ? cap.additional_service_capabilities
                : [],
            operationalByMode: (cap.operationalByMode || cap.operational_by_mode || next.capabilities?.operationalByMode || {}),
            performance: (() => {
              const perf = cap.performance || cap.performance_kpis || {};
              const prev =
                next.capabilities?.performance && typeof next.capabilities.performance === "object"
                  ? next.capabilities.performance
                  : {};
              const sl = perf.serviceLevels || perf.service_levels || {};
              const prevSl = prev.serviceLevels && typeof prev.serviceLevels === "object" ? prev.serviceLevels : {};
              const ind = perf.industryTargets || perf.industry_targets || perf.benchmarks || perf.benchmark || {};
              const prevInd = prev.industryTargets && typeof prev.industryTargets === "object" ? prev.industryTargets : {};
              return {
                ...prev,
                ...perf,
                industryTargets: { ...prevInd, ...ind },
                serviceLevels: { ...prevSl, ...sl },
              };
            })(),
            customerReviews: (() => {
              const cr = cap.customerReviews || cap.customer_reviews || {};
              const prev =
                next.capabilities?.customerReviews && typeof next.capabilities.customerReviews === "object"
                  ? next.capabilities.customerReviews
                  : {};
              const prevDist =
                prev.ratingDistribution && typeof prev.ratingDistribution === "object" ? prev.ratingDistribution : {};
              const dist = cr.ratingDistribution || cr.rating_distribution || {};
              const items = Array.isArray(cr.reviews)
                ? cr.reviews
                : Array.isArray(cr.reviews_list)
                  ? cr.reviews_list
                  : Array.isArray(cr.items)
                    ? cr.items
                    : [];
              const prevItems = Array.isArray(prev.reviews) ? prev.reviews : [];
              const normRow = (row) => {
                if (!row || typeof row !== "object") return null;
                const ratingRaw = row.rating ?? row.stars;
                const ratingNum = ratingRaw === "" || ratingRaw == null ? NaN : Number(ratingRaw);
                const rating = Number.isFinite(ratingNum) ? Math.min(5, Math.max(1, Math.round(ratingNum))) : 5;
                const helpfulRaw = row.helpfulCount ?? row.helpful_count;
                const helpfulNum = helpfulRaw === "" || helpfulRaw == null ? 0 : Number(helpfulRaw);
                return {
                  companyName: String(row.companyName ?? row.company_name ?? row.reviewerCompany ?? "").trim(),
                  industry: String(row.industry ?? row.reviewer_industry ?? "").trim(),
                  reviewDate: String(row.reviewDate ?? row.review_date ?? row.date ?? row.createdAt ?? "").trim(),
                  rating,
                  helpfulCount: Number.isFinite(helpfulNum) && helpfulNum >= 0 ? Math.round(helpfulNum) : 0,
                  isVerified: !!(row.isVerified ?? row.verified ?? row.is_verified),
                  comment: String(row.comment ?? row.body ?? row.text ?? "").trim(),
                };
              };
              const mergedList = (items.length ? items : prevItems).map(normRow).filter(Boolean);
              return {
                ...prev,
                ...cr,
                overallRating: cr.overallRating ?? cr.overall_rating ?? prev.overallRating ?? "",
                totalReviewsCount: cr.totalReviewsCount ?? cr.total_reviews_count ?? prev.totalReviewsCount ?? "",
                ratingDistribution: {
                  5: "",
                  4: "",
                  3: "",
                  2: "",
                  1: "",
                  ...prevDist,
                  ...dist,
                },
                reviews: mergedList,
              };
            })(),
            commercialTerms: (() => {
              const ct = cap.commercialTerms || cap.commercial_terms || {};
              return {
                ...(next.capabilities?.commercialTerms || {}),
                pricingModelsOffered: Array.isArray(ct.pricingModelsOffered)
                  ? ct.pricingModelsOffered
                  : Array.isArray(ct.pricing_models_offered)
                    ? ct.pricing_models_offered
                    : [],
                paymentTerms: Array.isArray(ct.paymentTerms)
                  ? ct.paymentTerms
                  : Array.isArray(ct.payment_terms)
                    ? ct.payment_terms
                    : [],
                creditPeriodDays: ct.creditPeriodDays ?? ct.credit_period_days ?? "",
                creditPeriodLabel: ct.creditPeriodLabel ?? ct.credit_period_label ?? "",
                currenciesAccepted: Array.isArray(ct.currenciesAccepted)
                  ? ct.currenciesAccepted
                  : Array.isArray(ct.currencies_accepted)
                    ? ct.currencies_accepted
                    : [],
                commercialSupport: Array.isArray(ct.commercialSupport)
                  ? ct.commercialSupport
                  : Array.isArray(ct.commercial_support)
                    ? ct.commercial_support
                    : [],
                minimumRequirements: Array.isArray(ct.minimumRequirements)
                  ? ct.minimumRequirements
                  : Array.isArray(ct.minimum_requirements)
                    ? ct.minimum_requirements
                    : [],
              };
            })(),
          };
          next.fleet_assets = {
            ...next.fleet_assets,
            ...(fa || {}),
            warehousesStorage: {
              ...(next.fleet_assets?.warehousesStorage || {}),
              ...(fa.warehousesStorage || fa.warehouses_storage || {}),
              totalWarehouses:
                (fa?.warehousesStorage?.totalWarehouses ??
                  fa?.warehouses_storage?.totalWarehouses ??
                  fa?.warehouses_storage?.total_warehouses ??
                  fa?.totalWarehouses ??
                  "") ?? "",
              totalAreaSqFt:
                (fa?.warehousesStorage?.totalAreaSqFt ??
                  fa?.warehouses_storage?.totalAreaSqFt ??
                  fa?.warehouses_storage?.total_area_sq_ft ??
                  fa?.totalAreaSqFt ??
                  "") ?? "",
              coldStorageUnits:
                (fa?.warehousesStorage?.coldStorageUnits ??
                  fa?.warehouses_storage?.coldStorageUnits ??
                  fa?.warehouses_storage?.cold_storage_units ??
                  fa?.coldStorageUnits ??
                  "") ?? "",
              bondedWarehouses:
                (fa?.warehousesStorage?.bondedWarehouses ??
                  fa?.warehouses_storage?.bondedWarehouses ??
                  fa?.warehouses_storage?.bonded_warehouses ??
                  fa?.bondedWarehouses ??
                  "") ?? "",
            },
          };
          const cr = d.coverage_routes || {};
          const parseMaybeCsv = (v) =>
            typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : [];
          const normalizePorts = (raw) => {
            const arr = Array.isArray(raw) ? raw : [];
            return arr
              .map((p) => {
                if (typeof p === "string") return { name: p, portType: "", traffic: "" };
                if (p && typeof p === "object") {
                  return {
                    name: String(p.name ?? p.portName ?? p.port_name ?? "").trim(),
                    portType: String(p.portType ?? p.port_type ?? p.type ?? "").trim(),
                    traffic: String(p.traffic ?? p.trafficLevel ?? p.traffic_level ?? "").trim(),
                  };
                }
                return null;
              })
              .filter((x) => x && String(x.name || "").trim() !== "");
          };
          const normalizeCorridors = (raw) => {
            const arr = Array.isArray(raw) ? raw : [];
            return arr
              .map((c) => {
                if (typeof c === "string") {
                  return { name: c, modes: [], frequency: "", traffic: "" };
                }
                if (c && typeof c === "object") {
                  const modesRaw = c.modes ?? c.transportModes ?? c.transport_modes ?? c.transport_modes_map;
                  const normalizeMode = (m) => {
                    const s = String(m || "").trim().toLowerCase();
                    if (s === "sea") return "ocean";
                    if (s === "multi-modal" || s === "multimodal") return "multimodal";
                    return s;
                  };
                  const modes = Array.isArray(modesRaw)
                    ? modesRaw.map(normalizeMode).filter(Boolean)
                    : typeof modesRaw === "string"
                      ? modesRaw
                          .split(",")
                          .map((x) => normalizeMode(x))
                          .filter(Boolean)
                      : [];
                  return {
                    name: String(c.name ?? c.laneName ?? c.lane_name ?? "").trim(),
                    modes,
                    frequency: String(c.frequency ?? c.freaquency ?? c.freq ?? "").trim(),
                    traffic: String(c.traffic ?? c.trafficLevel ?? c.traffic_level ?? "").trim(),
                  };
                }
                return null;
              })
              .filter((x) => x && String(x.name || "").trim() !== "");
          };
          next.coverage_routes = {
            ...next.coverage_routes,
            geographicCoverage: Array.isArray(cr.geographicCoverage)
              ? cr.geographicCoverage
              : Array.isArray(cr.geographic_coverage)
                ? cr.geographic_coverage
                : Array.isArray(cr.regionsServed)
                  ? cr.regionsServed
                  : Array.isArray(cr.regions_served)
                    ? cr.regions_served
                    : parseMaybeCsv(cr.regionsServed || cr.regions_served),
            majorTradeLanes: Array.isArray(cr.majorTradeLanes)
              ? cr.majorTradeLanes
              : Array.isArray(cr.major_trade_lanes)
                ? cr.major_trade_lanes
                : Array.isArray(cr.keyLanes)
                  ? cr.keyLanes
                  : Array.isArray(cr.key_lanes)
                    ? cr.key_lanes
                    : parseMaybeCsv(cr.keyLanes || cr.key_lanes),
            portsTerminals: normalizePorts(cr.portsTerminals || cr.ports_terminals || cr.ports || []),
            airportNetwork: Array.isArray(cr.airportNetwork)
              ? cr.airportNetwork
              : Array.isArray(cr.airport_network)
                ? cr.airport_network
                : Array.isArray(cr.airports)
                  ? cr.airports
                  : [],
            corridors: normalizeCorridors(
              cr.corridors || cr.majorCorridors || cr.major_corridors || cr.tradeCorridors || cr.trade_corridors || []
            ),
          };
          const cert = d.certifications || {};
          const regs = cert.registrations || cert.companyRegistrations || cert.company_registrations || {};
          const industry =
            cert.industryCertifications || cert.industry_certifications || cert.certifications || [];
          next.certifications = {
            ...next.certifications,
            registrations: {
              ...(next.certifications?.registrations || {}),
              cinNumber: regs.cinNumber || regs.cin_number || "",
              gstin: regs.gstin || regs.GSTIN || "",
              pan: regs.pan || regs.PAN || "",
              iec: regs.iec || regs.importExportCode || regs.import_export_code || "",
            },
            industryCertifications: Array.isArray(industry) ? industry : [],
          };
        }

        return applyTradeCompanyPrefill(next, profile, StorageService.getUser());
      });
    } catch (e) {
      // ignore (no profile yet)
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    // Guard: only allow Logistics users to access this page
    const user = StorageService.getUser();
    if (!user) {
      navigate("/login");
      return;
    }
    loadExisting();
    // Load master languages list for Contact tab
    (async () => {
      try {
        const res = await CommonService.get("/master/languages");
        const rows = res?.data ?? [];
        const list = Array.isArray(rows) ? rows : [];
        setLanguageOptions(
          list
            .map((r) => {
              const label = r.language_name ?? r.language ?? r.name ?? r.label;
              const raw = r.flag;
              let displayFlag = "";
              if (raw != null && String(raw).trim() !== "") {
                const s = String(raw).trim();
                // DB often uses flag as active bit (0/1); only use as label prefix if it looks like an icon/text flag
                if (!/^[01]$/.test(s) && Number.isNaN(Number(s))) {
                  displayFlag = s;
                }
              }
              return { id: r.language_id ?? r.id, label, flag: displayFlag };
            })
            .filter((x) => x.label)
        );
      } catch (e) {
        // fallback to previous static options if API not available
        setLanguageOptions([
          { id: 1, label: "English", flag: "" },
          { id: 2, label: "Hindi", flag: "" },
          { id: 3, label: "Marathi", flag: "" },
          { id: 4, label: "Gujarati", flag: "" },
          { id: 5, label: "Tamil", flag: "" },
          { id: 6, label: "Telugu", flag: "" },
          { id: 7, label: "Kannada", flag: "" },
          { id: 8, label: "Bengali", flag: "" },
        ]);
      }
    })();
    (async () => {
      try {
        const res = await CommonService.get("/master/factory-ShippingTerms");
        const data = res?.data ?? {};
        const list = data.paymentSchedule || data.master_payment_schedule || [];
        const arr = Array.isArray(list) ? list : [];
        setPaymentTermOptions(
          arr
            .map((r) => ({
              id: r.payment_schedule_id ?? r.id,
              label: r.payment_schedule ?? r.schedule_name ?? r.label ?? r.name,
            }))
            .filter((x) => x.label)
        );
      } catch (e) {
        setPaymentTermOptions([]);
      }
    })();
    (async () => {
      try {
        const res = await CommonService.get("/master/currencies");
        const raw = res?.data;
        const list = Array.isArray(raw) ? raw : raw?.currencies || raw?.master_currencies || [];
        setCurrencyOptions(
          list
            .map((r) => ({
              id: r.currency_id ?? r.id,
              code: String(r.currency_code || r.code || "").trim(),
              symbol: r.currency_symbol || r.symbol || "",
            }))
            .filter((x) => x.code)
        );
      } catch (e) {
        setCurrencyOptions([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Mandatory field checks (matches * labels). Returns { message, section } or null if valid.
   * `section` is a SECTIONS key so Save can switch the user to the tab that needs attention.
   */
  const validate = () => {
    const bi = form.basic_information || {};
    const companyName = String(bi.companyName || "").trim();
    const companyType = String(bi.companyType || "").trim();
    const years = bi.yearsInBusiness;
    const hq = String(bi.headquartersLocation || "").trim();
    const about = String(bi.aboutCompany || "").trim();
    const modes = Array.isArray(form.transport_modes?.modes) ? form.transport_modes.modes : [];
    const cd = form.contact_details || {};
    const primaryName = String(cd.primaryName || "").trim();
    const primaryDesignation = String(cd.primaryDesignation || "").trim();
    const primaryEmail = String(cd.primaryEmail || "").trim();
    const primaryPhone = String(cd.primaryPhone || "").trim();
    const phoneDigits = primaryPhone.replace(/\D/g, "");

    if (!companyName) return { message: "Company Name is required", section: "basic" };
    if (!companyType) return { message: "Company Type is required", section: "basic" };
    if (years === "" || years == null || Number.isNaN(Number(years)) || Number(years) < 0) {
      return { message: "Years in Business is required (0 or a positive number)", section: "basic" };
    }
    if (!hq) return { message: "Headquarters Location is required", section: "basic" };
    if (!about) return { message: "About the Company is required", section: "basic" };
    if (modes.length === 0) {
      return { message: "Please select at least one Transport Mode", section: "transport" };
    }
    if (!primaryName) return { message: "Primary Contact Name is required", section: "contact" };
    if (!primaryDesignation) return { message: "Role/Designation is required", section: "contact" };
    if (!primaryEmail) return { message: "Email Address is required", section: "contact" };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(primaryEmail)) {
      return { message: "Please enter a valid Email Address", section: "contact" };
    }
    if (!primaryPhone.trim()) return { message: "Phone Number is required", section: "contact" };
    if (phoneDigits.length < 8) {
      return { message: "Phone Number must include at least 8 digits", section: "contact" };
    }
    return null;
  };

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    // Backend requires upload metadata (see upload.validator.ts)
    fd.append("category", "company");
    fd.append(
      "companyName",
      String(form?.basic_information?.companyName || StorageService.getUser()?.company_name || "Company").trim()
    );
    fd.append("fileType", "company_logo");
    const resp = await CommonService.post("/upload/single", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const raw =
      resp?.data?.accessPath ||
      resp?.data?.path ||
      resp?.data?.url ||
      resp?.data?.location ||
      resp?.data?.filePath;
    if (!raw) throw new Error("Upload succeeded but no file path returned");
    // Prefer server public path; normalize windows slashes if any.
    return String(raw).replace(/\\/g, "/");
  };

  const handleSave = async () => {
    const invalid = validate();
    if (invalid) {
      toast.error(invalid.message);
      if (invalid.section && SECTIONS.some((s) => s.key === invalid.section)) {
        setActiveSection(invalid.section);
        setActiveTabId(resolveTabIdForSection(invalid.section));
        setMergedTabView(false);
        scrollCardIntoView();
      }
      return;
    }

    setLoading(true);
    try {
      const cid = companyId || (await loadCompanyId());
      if (!cid) {
        toast.error("Please complete your Trade Profile first, then return to Logistics profile.");
        return;
      }
      if (!companyId && cid) setCompanyId(cid);

      const payload = {
        basic_information: form.basic_information,
        transport_modes: form.transport_modes,
        contact_details: form.contact_details,
        capabilities: form.capabilities,
        fleet_assets: form.fleet_assets,
        coverage_routes: form.coverage_routes,
        certifications: form.certifications,
      };

      const resp = await CommonService.put(`/company/profile/${cid}/logistics`, payload);
      if (resp?.success) {
        toast.success(resp?.message || "Logistics profile saved successfully");
        await loadExisting({ silent: true });

        if (invitePopup && window.opener && !window.opener.closed) {
          try {
            window.opener.postMessage(
              { type: "SA_LOGISTICS_PROVIDER_READY", companyId: cid },
              window.location.origin,
            );
          } catch {
            /* ignore */
          }
          toast.info("Profile saved. You can close this window and return to Invite Logistics Providers.");
          return;
        }

        if (onboarding === "logistics") {
          const { getDashboardRoute } = await import("../../utils/getDashboardRoute");
          navigate(getDashboardRoute());
        }
      } else {
        toast.error(resp?.message || "Failed to save Logistics profile");
      }
    } catch (e) {
      const data = e?.response?.data;
      const msg =
        (typeof data?.message === "string" && data.message) ||
        (Array.isArray(data?.errors) && data.errors.join(". ")) ||
        e?.message ||
        "Failed to save Logistics profile";
      toast.error(msg);
      const serverSection = data?.section;
      if (serverSection && SECTIONS.some((s) => s.key === serverSection)) {
        setActiveSection(serverSection);
        setActiveTabId(resolveTabIdForSection(serverSection));
        setMergedTabView(false);
        scrollCardIntoView();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lp-page lp-page--edit">
      <nav className="lp-breadcrumb" aria-label="Breadcrumb">
        <Link to="/hub-home">Home</Link>
        <span className="lp-breadcrumb__sep" aria-hidden>
          &gt;
        </span>
        <Link to="/logistics-profile">Logistics Profile</Link>
        <span className="lp-breadcrumb__sep" aria-hidden>
          &gt;
        </span>
        <span aria-current="page">Edit Profile</span>
      </nav>

      <header className="lp-page-header">
        <div className="lp-page-header__copy">
          <h1 className="lp-page-header__title">{pageHeading}</h1>
          <p className="lp-page-header__lead">{pageLead}</p>
        </div>
        <Link to="/hub-home" className="lp-back-dashboard">
          ← Back to Dashboard
        </Link>
      </header>

      <div className="lp-hero-wrap">
        <div className="lp-hero-strip">
          {LP_HERO_BENEFITS.map((item) => {
            const HeroIcon = item.Icon;
            return (
              <div key={item.n} className="lp-hero-strip__item">
                <div className="lp-hero-strip__icon-wrap" aria-hidden>
                  <HeroIcon className="lp-hero-strip__icon" />
                </div>
                <span className="lp-hero-strip__num">{item.n}</span>
                <span className="lp-hero-strip__title">{item.title}</span>
                <span className="lp-hero-strip__text">{item.text}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="lp-tabs-bar-outer">
        <div className="lp-tabs-bar">
          <div className="lp-tabs" role="tablist" aria-label="Profile sections">
          {PROFILE_TABS.map((tab) => {
            const TabIcon = tab.TabIcon;
            const isActive = activeTabId === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`lp-tab ${isActive ? "lp-tab--active" : ""}`}
                onClick={() => selectTab(tab)}
              >
                <TabIcon className="lp-tab__icon" aria-hidden />
                <span>{tab.label}</span>
              </button>
            );
          })}
          </div>
        </div>
      </div>

      <div className="lp-shell lp-shell--tabs-only">
        <main className="lp-main">
          <div className="lp-card" ref={lpCardRef}>
            {showSection("basic") && (
              <div className="lp-card-header">
                <div className="lp-card-title">{sectionMeta.label}</div>
                <div className="lp-card-desc">Company details and overview</div>
              </div>
            )}

            {showSection("basic") && (
              <div className="lp-form">
                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">
                      Company Name <span className="lp-required">*</span>
                    </label>
                    <input
                      className="form-control lp-input-pill"
                      placeholder="e.g., Global Freight Solutions Pvt. Ltd."
                      value={form.basic_information.companyName}
                      onChange={(e) => setNested("basic_information", "companyName", e.target.value)}
                    />
                  </div>
                </div>

                <div className="lp-row lp-row-2 lp-row--company-logo">
                  <div className="lp-field">
                    <label className="lp-label">Company Logo</label>
                    <div className="lp-logo">
                      <div className="lp-logo-preview">
                        {form.basic_information.companyLogo ? (
                          <img
                            alt="Company logo"
                            src={resolvePublicFileUrl(form.basic_information.companyLogo)}
                          />
                        ) : (
                          <div className="lp-logo-empty">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                              <path
                                d="M12 3v12m0-12l4 4m-4-4L8 7M5 14v5h14v-5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div>
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/*"
                          className="d-none"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              setLoading(true);
                              const path = await uploadLogo(file);
                              setNested("basic_information", "companyLogo", path);
                              toast.success("Logo uploaded");
                            } catch (err) {
                              toast.error(err?.message || "Failed to upload logo");
                            } finally {
                              setLoading(false);
                              if (logoInputRef.current) logoInputRef.current.value = "";
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={loading}
                        >
                          Upload Logo
                        </button>
                        <div className="lp-help">Recommended: 400x400px, PNG or JPG</div>
                      </div>
                    </div>
                  </div>

                  <div className="lp-field">
                    <label className="lp-label">
                      Company Type <span className="lp-required">*</span>
                    </label>
                    <Select
                      className="lp-company-type-select"
                      classNamePrefix="lp-ct"
                      placeholder="Select company type"
                      options={COMPANY_TYPE_SELECT_OPTIONS}
                      value={companyTypeSelectValue}
                      onChange={(opt) =>
                        setNested("basic_information", "companyType", opt?.value ?? "")
                      }
                      isSearchable
                      isClearable={false}
                    />
                  </div>

                  <div className="lp-field">
                    <label className="lp-label">
                      Years in Business <span className="lp-required">*</span>
                    </label>
                    <input
                      className="form-control lp-input-pill"
                      placeholder="e.g., 15"
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      value={form.basic_information.yearsInBusiness}
                      onChange={(e) =>
                        setNested(
                          "basic_information",
                          "yearsInBusiness",
                          String(e.target.value ?? "").replace(/[^\d]/g, "")
                        )
                      }
                    />
                  </div>
                </div>

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">
                      Headquarters Location <span className="lp-required">*</span>
                    </label>
                    <input
                      className="form-control lp-input-pill"
                      placeholder="e.g., Mumbai, Maharashtra, India"
                      value={form.basic_information.headquartersLocation}
                      onChange={(e) => setNested("basic_information", "headquartersLocation", e.target.value)}
                    />
                  </div>
                </div>

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">
                      About the Company <span className="lp-required">*</span>
                    </label>
                    <textarea
                      className="form-control lp-input-pill"
                      rows={4}
                      maxLength={500}
                      placeholder="Describe your company, its history, mission, and what makes you unique in the logistics industry..."
                      value={form.basic_information.aboutCompany}
                      onChange={(e) => setNested("basic_information", "aboutCompany", e.target.value)}
                    />
                    <div className="lp-charcount">
                      {(form.basic_information.aboutCompany || "").length} / 500 characters
                    </div>
                  </div>
                </div>

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">Operating Model</label>
                    <input
                      className="form-control lp-input-pill"
                      placeholder="e.g., Asset Owner + Freight Forwarder"
                      value={form.basic_information.operatingModel}
                      onChange={(e) => setNested("basic_information", "operatingModel", e.target.value)}
                    />
                  </div>
                </div>

              </div>
            )}

            {showSection("transport") && (
              <div className="lp-form">
                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-tab-title">Transport Modes &amp; Services</div>
                    <div className="lp-tab-subtitle">Select the transport modes you operate and provide service details</div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">
                      Transport Modes <span className="lp-required">*</span>
                    </label>
                    <div className="lp-muted">Select all transport modes you offer</div>
                    <div className="lp-mode-cards">
                      {LOGISTICS_TRANSPORT_MODES.map((m) => {
                        const selected = (form.transport_modes?.modes || []).includes(m.id);
                        return (
                          <button
                            key={m.id}
                            type="button"
                            className={`lp-mode-card ${selected ? "active" : ""}`}
                            onClick={() => toggleNestedArrayValue("transport_modes", "modes", m.id)}
                          >
                            <div className="lp-mode-icon" aria-hidden="true">{m.icon}</div>
                            <div className="lp-mode-label">{m.label}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">Preferred Shipment Types</label>
                    <div className="lp-ship-grid">
                      {preferredShipmentTypeOptions.map((t) => {
                        const checked = (form.transport_modes?.preferredShipmentTypes || []).includes(t);
                        return (
                          <label key={t} className="lp-ship-item">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleNestedArrayValue("transport_modes", "preferredShipmentTypes", t)}
                            />
                            <span>{t}</span>
                          </label>
                        );
                      })}
                    </div>
                    {preferredShipmentTypeOptions.length === 0 ? (
                      <div className="text-muted small mt-2">Select a transport mode above to see shipment types.</div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {showSection("contact") && (
              <div className="lp-form">
                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-tab-title">Contact Information</div>
                    <div className="lp-tab-subtitle">Primary contact person and business details</div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row lp-row-2">
                  <div className="lp-field">
                    <label className="lp-label">
                      Primary Contact Name <span className="lp-required">*</span>
                    </label>
                    <input
                      className="form-control lp-input-pill"
                      placeholder="e.g., Rajesh Kumar"
                      value={form.contact_details.primaryName}
                      onChange={(e) => setNested("contact_details", "primaryName", e.target.value)}
                    />
                  </div>
                  <div className="lp-field">
                    <label className="lp-label">
                      Role/Designation <span className="lp-required">*</span>
                    </label>
                    <input
                      className="form-control lp-input-pill"
                      placeholder="e.g., Senior Business Development Manager"
                      value={form.contact_details.primaryDesignation}
                      onChange={(e) => setNested("contact_details", "primaryDesignation", e.target.value)}
                    />
                  </div>
                  <div className="lp-field">
                    <label className="lp-label">
                      Email Address <span className="lp-required">*</span>
                    </label>
                    <input
                      className="form-control lp-input-pill"
                      type="email"
                      placeholder="e.g., contact@company.com"
                      value={form.contact_details.primaryEmail}
                      onChange={(e) => setNested("contact_details", "primaryEmail", e.target.value)}
                    />
                  </div>
                </div>

                <div className="lp-row lp-row-phone-lang">
                  <div className="lp-field">
                    <label className="lp-label">
                      Phone Number <span className="lp-required">*</span>
                    </label>
                    <input
                      className="form-control lp-input-pill"
                      placeholder="e.g., +91 98765 43210"
                      value={form.contact_details.primaryPhone}
                      onChange={(e) =>
                        setNested("contact_details", "primaryPhone", e.target.value.replace(/[^\d+() -]/g, ""))
                      }
                    />
                  </div>
                  <div className="lp-field">
                    <label className="lp-label">Languages Supported</label>
                    <Select
                      isMulti
                      className="lp-lang-select"
                      classNamePrefix="lp-lang-rs"
                      placeholder="Select languages supported"
                      options={languageSelectOptions}
                      value={languagesSupportedSelectValue}
                      onChange={(opts) =>
                        setNested(
                          "contact_details",
                          "languagesSupported",
                          (opts || []).map((o) => o.value)
                        )
                      }
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      isClearable
                      noOptionsMessage={() => "No languages loaded"}
                    />
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-toggle-card">
                      <div>
                        <div className="lp-toggle-title">24/7 Customer Support</div>
                        <div className="lp-toggle-subtitle">Do you provide round-the-clock support?</div>
                      </div>
                      <label className="lp-switch">
                        <input
                          type="checkbox"
                          checked={!!form.contact_details.customerSupport247}
                          onChange={(e) =>
                            setNested("contact_details", "customerSupport247", e.target.checked)
                          }
                        />
                        <span className="lp-slider" />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-tab-title">Office Locations (Optional)</div>
                    <div className="lp-tab-subtitle">Shown on the public Contact tab</div>
                  </div>
                </div>

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-muted mb-2">Add locations (Name, Address, Phone)</div>
                    <div className="lp-row lp-row-2">
                      <div className="lp-field">
                        <input
                          className="form-control lp-input-pill"
                          placeholder="Location name (e.g., Mumbai HQ)"
                          value={officeDraft.name}
                          onChange={(e) => setOfficeDraft((p) => ({ ...p, name: e.target.value }))}
                        />
                      </div>
                      <div className="lp-field">
                        <input
                          className="form-control lp-input-pill"
                          placeholder="Address"
                          value={officeDraft.address}
                          onChange={(e) => setOfficeDraft((p) => ({ ...p, address: e.target.value }))}
                        />
                      </div>
                      <div className="lp-field">
                        <input
                          className="form-control lp-input-pill"
                          placeholder="Phone"
                          value={officeDraft.phone}
                          onChange={(e) => setOfficeDraft((p) => ({ ...p, phone: e.target.value }))}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm mt-2"
                      onClick={() => {
                        const name = String(officeDraft.name || "").trim();
                        const address = String(officeDraft.address || "").trim();
                        const phone = String(officeDraft.phone || "").trim();
                        if (!name && !address && !phone) return;
                        setForm((p) => {
                          const cur = Array.isArray(p.contact_details?.officeLocations)
                            ? p.contact_details.officeLocations
                            : [];
                          return {
                            ...p,
                            contact_details: {
                              ...(p.contact_details || {}),
                              officeLocations: [...cur, { name, address, phone }],
                            },
                          };
                        });
                        setOfficeDraft({ name: "", address: "", phone: "" });
                      }}
                    >
                      Add location
                    </button>

                    {(form.contact_details?.officeLocations || []).length ? (
                      <div className="mt-3">
                        {(form.contact_details.officeLocations || []).map((loc, idx) => (
                          <div key={idx} className="lp-cert-row">
                            <div className="lp-cert-name">
                              <strong>{loc?.name || "Office"}</strong>
                              <div className="small text-muted">{loc?.address || ""}</div>
                              <div className="small text-muted">{loc?.phone || ""}</div>
                            </div>
                            <button
                              type="button"
                              className="btn btn-link text-danger p-0"
                              onClick={() =>
                                setForm((p) => {
                                  const cur = Array.isArray(p.contact_details?.officeLocations)
                                    ? p.contact_details.officeLocations
                                    : [];
                                  return {
                                    ...p,
                                    contact_details: {
                                      ...(p.contact_details || {}),
                                      officeLocations: cur.filter((_, i) => i !== idx),
                                    },
                                  };
                                })
                              }
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted small mt-2">No office locations added</div>
                    )}
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-tab-title">Business Hours (Optional)</div>
                  </div>
                </div>

                <div className="lp-row lp-row-2">
                  <div className="lp-field">
                    <label className="lp-label">Monday - Friday</label>
                    <input
                      className="form-control lp-input-pill"
                      placeholder="e.g., 9:00 AM - 7:00 PM"
                      value={form.contact_details?.businessHours?.monFri || ""}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          contact_details: {
                            ...(p.contact_details || {}),
                            businessHours: {
                              ...(p.contact_details?.businessHours || {}),
                              monFri: e.target.value,
                            },
                          },
                        }))
                      }
                    />
                  </div>
                  <div className="lp-field">
                    <label className="lp-label">Saturday</label>
                    <input
                      className="form-control lp-input-pill"
                      placeholder="e.g., 9:00 AM - 2:00 PM"
                      value={form.contact_details?.businessHours?.saturday || ""}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          contact_details: {
                            ...(p.contact_details || {}),
                            businessHours: {
                              ...(p.contact_details?.businessHours || {}),
                              saturday: e.target.value,
                            },
                          },
                        }))
                      }
                    />
                  </div>
                  <div className="lp-field">
                    <label className="lp-label">Sunday</label>
                    <input
                      className="form-control lp-input-pill"
                      placeholder="e.g., Closed"
                      value={form.contact_details?.businessHours?.sunday || ""}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          contact_details: {
                            ...(p.contact_details || {}),
                            businessHours: {
                              ...(p.contact_details?.businessHours || {}),
                              sunday: e.target.value,
                            },
                          },
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="lp-row">
                  <div className="lp-field" style={{ gridColumn: "1 / -1" }}>
                    <label className="lp-label">Contact card hours line (optional)</label>
                    <input
                      className="form-control lp-input-pill"
                      placeholder='e.g. Mon-Sat: 9:00 AM - 7:00 PM IST (shown on public Contact tab; overrides auto text from business hours)'
                      value={form.contact_details?.availabilitySummary || ""}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          contact_details: {
                            ...(p.contact_details || {}),
                            availabilitySummary: e.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {showSection("capabilities") && (
              <div className="lp-section-block lp-section-block--capabilities">
              <div className="lp-form">
                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-tab-title">Service Capabilities</div>
                    <div className="lp-tab-subtitle">Core specializations and industries you serve</div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">Core Specialization</label>
                    <div className="lp-two-col-checks">
                      {LOGISTICS_CORE_SPECIALIZATION.map((x) => {
                        const checked = (form.capabilities?.coreSpecialization || []).includes(x);
                        return (
                          <label key={x} className="lp-checkline">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleNestedArrayValue("capabilities", "coreSpecialization", x)}
                            />
                            <span>{x}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">Industries Served</label>
                    <div className="lp-industry-grid">
                      {LOGISTICS_INDUSTRIES_SERVED.map((x) => {
                        const checked = (form.capabilities?.industriesServed || []).includes(x);
                        return (
                          <label key={x} className="lp-checkline">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleNestedArrayValue("capabilities", "industriesServed", x)}
                            />
                            <span>{x}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">Special Cargo Capabilities</label>
                    <div className="lp-two-col-checks">
                      {LOGISTICS_SPECIAL_CARGO.map((x) => {
                        const checked = (form.capabilities?.specialCargoCapabilities || []).includes(x);
                        return (
                          <label key={x} className="lp-checkline">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleNestedArrayValue("capabilities", "specialCargoCapabilities", x)}
                            />
                            <span>{x}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-subsection-title">Operational Capabilities by Transport Mode</div>
                    <div className="lp-muted mb-2">Add service details shown on the Capability tab (view profile)</div>

                    {(() => {
                      const selectedModes = Array.isArray(form.transport_modes?.modes) ? form.transport_modes.modes : [];
                      const hasAny = selectedModes.includes("road") || selectedModes.includes("ocean") || selectedModes.includes("air");
                      if (hasAny) return null;
                      return (
                        <div className="text-muted small mb-3">
                          Select your transport modes in the <strong>Transport Modes &amp; Services</strong> tab to configure operational
                          capabilities here.
                        </div>
                      );
                    })()}

                    {/* Road */}
                    {(form.transport_modes?.modes || []).includes("road") && (
                    <div className="lp-subsection mb-3">
                      <div className="lp-subsection-title">Road Transport</div>
                      <div className="lp-metrics-grid">
                        <div className="lp-metric">
                          <div className="lp-metric-label">Service Types</div>
                          <Select
                            isMulti
                            className="lp-lang-select"
                            classNamePrefix="lp-lang-rs"
                            placeholder="Select service types"
                            options={modeServiceTypeSelectOptions.road}
                            value={modeServiceTypesSelectValue("road")}
                            onChange={(opts) =>
                              setForm((p) => ({
                                ...p,
                                capabilities: {
                                  ...(p.capabilities || {}),
                                  operationalByMode: {
                                    ...(p.capabilities?.operationalByMode || {}),
                                    road: {
                                      ...(p.capabilities?.operationalByMode?.road || {}),
                                      serviceTypes: (opts || []).map((o) => o.value),
                                    },
                                  },
                                },
                              }))
                            }
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            isClearable
                            noOptionsMessage={() => "No service types"}
                          />
                        </div>
                        <div className="lp-metric">
                          <div className="lp-metric-label">Coverage</div>
                          <input
                            className="form-control lp-input-pill"
                            placeholder="e.g., Pan India + SAARC"
                            value={form.capabilities?.operationalByMode?.road?.coverage || ""}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                capabilities: {
                                  ...(p.capabilities || {}),
                                  operationalByMode: {
                                    ...(p.capabilities?.operationalByMode || {}),
                                    road: {
                                      ...(p.capabilities?.operationalByMode?.road || {}),
                                      coverage: e.target.value,
                                    },
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="lp-metric">
                          <div className="lp-metric-label">Special Cargo</div>
                          <input
                            className="form-control lp-input-pill"
                            placeholder="e.g., Reefer, ODC"
                            value={form.capabilities?.operationalByMode?.road?.specialCargo || ""}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                capabilities: {
                                  ...(p.capabilities || {}),
                                  operationalByMode: {
                                    ...(p.capabilities?.operationalByMode || {}),
                                    road: {
                                      ...(p.capabilities?.operationalByMode?.road || {}),
                                      specialCargo: e.target.value,
                                    },
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="lp-metric">
                          <div className="lp-metric-label">GPS Tracking</div>
                          <label className="lp-checkline">
                            <input
                              type="checkbox"
                              checked={!!form.capabilities?.operationalByMode?.road?.gpsTracking}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  capabilities: {
                                    ...(p.capabilities || {}),
                                    operationalByMode: {
                                      ...(p.capabilities?.operationalByMode || {}),
                                      road: {
                                        ...(p.capabilities?.operationalByMode?.road || {}),
                                        gpsTracking: e.target.checked,
                                      },
                                    },
                                  },
                                }))
                              }
                            />
                            <span>Enabled</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    )}

                    {/* Ocean */}
                    {(form.transport_modes?.modes || []).includes("ocean") && (
                    <div className="lp-subsection mb-3">
                      <div className="lp-subsection-title">Ocean Freight</div>
                      <div className="lp-metrics-grid">
                        <div className="lp-metric">
                          <div className="lp-metric-label">Container Types</div>
                          <Select
                            isMulti
                            className="lp-lang-select"
                            classNamePrefix="lp-lang-rs"
                            placeholder="Select container types"
                            options={oceanContainerTypeSelectOptions}
                            value={oceanContainerTypesSelectValue}
                            onChange={(opts) =>
                              setForm((p) => ({
                                ...p,
                                capabilities: {
                                  ...(p.capabilities || {}),
                                  operationalByMode: {
                                    ...(p.capabilities?.operationalByMode || {}),
                                    ocean: {
                                      ...(p.capabilities?.operationalByMode?.ocean || {}),
                                      containerTypes: (opts || []).map((o) => o.value),
                                    },
                                  },
                                },
                              }))
                            }
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            isClearable
                            noOptionsMessage={() => "No container types"}
                          />
                        </div>
                        <div className="lp-metric">
                          <div className="lp-metric-label">Service Model</div>
                          <input
                            className="form-control lp-input-pill"
                            placeholder="e.g., FCL & LCL"
                            value={form.capabilities?.operationalByMode?.ocean?.serviceModel || ""}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                capabilities: {
                                  ...(p.capabilities || {}),
                                  operationalByMode: {
                                    ...(p.capabilities?.operationalByMode || {}),
                                    ocean: {
                                      ...(p.capabilities?.operationalByMode?.ocean || {}),
                                      serviceModel: e.target.value,
                                    },
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="lp-metric">
                          <div className="lp-metric-label">Special Units</div>
                          <input
                            className="form-control lp-input-pill"
                            placeholder="e.g., Open Top"
                            value={form.capabilities?.operationalByMode?.ocean?.specialCargo || ""}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                capabilities: {
                                  ...(p.capabilities || {}),
                                  operationalByMode: {
                                    ...(p.capabilities?.operationalByMode || {}),
                                    ocean: {
                                      ...(p.capabilities?.operationalByMode?.ocean || {}),
                                      specialCargo: e.target.value,
                                    },
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="lp-metric">
                          <div className="lp-metric-label">Customer Support</div>
                          <label className="lp-checkline">
                            <input
                              type="checkbox"
                              checked={!!form.capabilities?.operationalByMode?.ocean?.customerSupport}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  capabilities: {
                                    ...(p.capabilities || {}),
                                    operationalByMode: {
                                      ...(p.capabilities?.operationalByMode || {}),
                                      ocean: {
                                        ...(p.capabilities?.operationalByMode?.ocean || {}),
                                        customerSupport: e.target.checked,
                                      },
                                    },
                                  },
                                }))
                              }
                            />
                            <span>Available</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    )}

                    {/* Air */}
                    {(form.transport_modes?.modes || []).includes("air") && (
                    <div className="lp-subsection">
                      <div className="lp-subsection-title">Air Freight</div>
                      <div className="lp-metrics-grid">
                        <div className="lp-metric">
                          <div className="lp-metric-label">Service Types</div>
                          <Select
                            isMulti
                            className="lp-lang-select"
                            classNamePrefix="lp-lang-rs"
                            placeholder="Select service types"
                            options={modeServiceTypeSelectOptions.air}
                            value={modeServiceTypesSelectValue("air")}
                            onChange={(opts) =>
                              setForm((p) => ({
                                ...p,
                                capabilities: {
                                  ...(p.capabilities || {}),
                                  operationalByMode: {
                                    ...(p.capabilities?.operationalByMode || {}),
                                    air: {
                                      ...(p.capabilities?.operationalByMode?.air || {}),
                                      serviceTypes: (opts || []).map((o) => o.value),
                                    },
                                  },
                                },
                              }))
                            }
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            isClearable
                            noOptionsMessage={() => "No service types"}
                          />
                        </div>
                        <div className="lp-metric">
                          <div className="lp-metric-label">Cargo Types</div>
                          <input
                            className="form-control lp-input-pill"
                            placeholder="e.g., General, Pharma"
                            value={form.capabilities?.operationalByMode?.air?.cargoTypes || ""}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                capabilities: {
                                  ...(p.capabilities || {}),
                                  operationalByMode: {
                                    ...(p.capabilities?.operationalByMode || {}),
                                    air: {
                                      ...(p.capabilities?.operationalByMode?.air || {}),
                                      cargoTypes: e.target.value,
                                    },
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="lp-metric">
                          <div className="lp-metric-label">Airlines / Partners</div>
                          <input
                            className="form-control lp-input-pill"
                            placeholder="e.g., 15+ Partners"
                            value={form.capabilities?.operationalByMode?.air?.airlines || ""}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                capabilities: {
                                  ...(p.capabilities || {}),
                                  operationalByMode: {
                                    ...(p.capabilities?.operationalByMode || {}),
                                    air: {
                                      ...(p.capabilities?.operationalByMode?.air || {}),
                                      airlines: e.target.value,
                                    },
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="lp-metric">
                          <div className="lp-metric-label">Temperature Control</div>
                          <label className="lp-checkline">
                            <input
                              type="checkbox"
                              checked={!!form.capabilities?.operationalByMode?.air?.temperatureControl}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  capabilities: {
                                    ...(p.capabilities || {}),
                                    operationalByMode: {
                                      ...(p.capabilities?.operationalByMode || {}),
                                      air: {
                                        ...(p.capabilities?.operationalByMode?.air || {}),
                                        temperatureControl: e.target.checked,
                                      },
                                    },
                                  },
                                }))
                              }
                            />
                            <span>Available</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    )}

                    {/* Rail */}
                    {(form.transport_modes?.modes || []).includes("rail") && (
                      <div className="lp-subsection mb-3">
                        <div className="lp-subsection-title">Rail</div>
                        <div className="lp-metrics-grid">
                          <div className="lp-metric">
                            <div className="lp-metric-label">Service Types</div>
                            <Select
                              isMulti
                              className="lp-lang-select"
                              classNamePrefix="lp-lang-rs"
                              placeholder="Select service types"
                              options={modeServiceTypeSelectOptions.rail}
                              value={modeServiceTypesSelectValue("rail")}
                              onChange={(opts) =>
                                setForm((p) => ({
                                  ...p,
                                  capabilities: {
                                    ...(p.capabilities || {}),
                                    operationalByMode: {
                                      ...(p.capabilities?.operationalByMode || {}),
                                      rail: {
                                        ...(p.capabilities?.operationalByMode?.rail || {}),
                                        serviceTypes: (opts || []).map((o) => o.value),
                                      },
                                    },
                                  },
                                }))
                              }
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              isClearable
                              noOptionsMessage={() => "No service types"}
                            />
                          </div>
                          <div className="lp-metric">
                            <div className="lp-metric-label">Coverage</div>
                            <input
                              className="form-control lp-input-pill"
                              placeholder="e.g., Major corridors + ICD network"
                              value={form.capabilities?.operationalByMode?.rail?.coverage || ""}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  capabilities: {
                                    ...(p.capabilities || {}),
                                    operationalByMode: {
                                      ...(p.capabilities?.operationalByMode || {}),
                                      rail: {
                                        ...(p.capabilities?.operationalByMode?.rail || {}),
                                        coverage: e.target.value,
                                      },
                                    },
                                  },
                                }))
                              }
                            />
                          </div>
                          <div className="lp-metric">
                            <div className="lp-metric-label">Special Cargo</div>
                            <input
                              className="form-control lp-input-pill"
                              placeholder="e.g., ODC, Reefer"
                              value={form.capabilities?.operationalByMode?.rail?.specialCargo || ""}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  capabilities: {
                                    ...(p.capabilities || {}),
                                    operationalByMode: {
                                      ...(p.capabilities?.operationalByMode || {}),
                                      rail: {
                                        ...(p.capabilities?.operationalByMode?.rail || {}),
                                        specialCargo: e.target.value,
                                      },
                                    },
                                  },
                                }))
                              }
                            />
                          </div>
                          <div className="lp-metric">
                            <div className="lp-metric-label">Tracking</div>
                            <label className="lp-checkline">
                              <input
                                type="checkbox"
                                checked={!!form.capabilities?.operationalByMode?.rail?.tracking}
                                onChange={(e) =>
                                  setForm((p) => ({
                                    ...p,
                                    capabilities: {
                                      ...(p.capabilities || {}),
                                      operationalByMode: {
                                        ...(p.capabilities?.operationalByMode || {}),
                                        rail: {
                                          ...(p.capabilities?.operationalByMode?.rail || {}),
                                          tracking: e.target.checked,
                                        },
                                      },
                                    },
                                  }))
                                }
                              />
                              <span>Enabled</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Multimodal */}
                    {(form.transport_modes?.modes || []).includes("multimodal") && (
                      <div className="lp-subsection mb-3">
                        <div className="lp-subsection-title">Multimodal</div>
                        <div className="lp-metrics-grid">
                          <div className="lp-metric">
                            <div className="lp-metric-label">Service Types</div>
                            <Select
                              isMulti
                              className="lp-lang-select"
                              classNamePrefix="lp-lang-rs"
                              placeholder="Select service types"
                              options={modeServiceTypeSelectOptions.multimodal}
                              value={modeServiceTypesSelectValue("multimodal")}
                              onChange={(opts) =>
                                setForm((p) => ({
                                  ...p,
                                  capabilities: {
                                    ...(p.capabilities || {}),
                                    operationalByMode: {
                                      ...(p.capabilities?.operationalByMode || {}),
                                      multimodal: {
                                        ...(p.capabilities?.operationalByMode?.multimodal || {}),
                                        serviceTypes: (opts || []).map((o) => o.value),
                                      },
                                    },
                                  },
                                }))
                              }
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              isClearable
                              noOptionsMessage={() => "No service types"}
                            />
                          </div>
                          <div className="lp-metric">
                            <div className="lp-metric-label">Coverage</div>
                            <input
                              className="form-control lp-input-pill"
                              placeholder="e.g., India + GCC + SEA"
                              value={form.capabilities?.operationalByMode?.multimodal?.coverage || ""}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  capabilities: {
                                    ...(p.capabilities || {}),
                                    operationalByMode: {
                                      ...(p.capabilities?.operationalByMode || {}),
                                      multimodal: {
                                        ...(p.capabilities?.operationalByMode?.multimodal || {}),
                                        coverage: e.target.value,
                                      },
                                    },
                                  },
                                }))
                              }
                            />
                          </div>
                          <div className="lp-metric">
                            <div className="lp-metric-label">Special Handling</div>
                            <input
                              className="form-control lp-input-pill"
                              placeholder="e.g., Reefer, ODC, Hazmat"
                              value={form.capabilities?.operationalByMode?.multimodal?.specialCargo || ""}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  capabilities: {
                                    ...(p.capabilities || {}),
                                    operationalByMode: {
                                      ...(p.capabilities?.operationalByMode || {}),
                                      multimodal: {
                                        ...(p.capabilities?.operationalByMode?.multimodal || {}),
                                        specialCargo: e.target.value,
                                      },
                                    },
                                  },
                                }))
                              }
                            />
                          </div>
                          <div className="lp-metric">
                            <div className="lp-metric-label">Tracking</div>
                            <label className="lp-checkline">
                              <input
                                type="checkbox"
                                checked={!!form.capabilities?.operationalByMode?.multimodal?.tracking}
                                onChange={(e) =>
                                  setForm((p) => ({
                                    ...p,
                                    capabilities: {
                                      ...(p.capabilities || {}),
                                      operationalByMode: {
                                        ...(p.capabilities?.operationalByMode || {}),
                                        multimodal: {
                                          ...(p.capabilities?.operationalByMode?.multimodal || {}),
                                          tracking: e.target.checked,
                                        },
                                      },
                                    },
                                  }))
                                }
                              />
                              <span>Enabled</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-subsection-title">Additional Service Capabilities</div>
                    <div className="lp-ship-grid">
                      {["Warehousing", "Customs & Compliance", "Insurance & Risk"].map((x) => {
                        const checked = (form.capabilities?.additionalServiceCapabilities || []).includes(x);
                        return (
                          <label key={x} className="lp-ship-item">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleNestedArrayValue("capabilities", "additionalServiceCapabilities", x)}
                            />
                            <span>{x}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              </div>
            )}

            {showSection("fleet") && (
              <div className="lp-form">
                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-tab-title">Fleet &amp; Assets</div>
                    <div className="lp-tab-subtitle">Details about your vehicles, containers, and infrastructure</div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-subsection">
                      <div className="lp-subsection-title">Total Trucks</div>
                      <div className="lp-metrics-grid">
                        {[
                          ["totalTrucks", "Total Trucks"],
                          ["trucksOwned", "Owned"],
                          ["trucksLeased", "Leased"],
                          ["trucksNetwork", "Network"],
                        ].map(([key, label]) => (
                          <div key={key} className="lp-metric">
                            <div className="lp-metric-label">{label}</div>
                            <input
                              className="form-control lp-input-pill"
                              type="number"
                              min={0}
                              step={1}
                              inputMode="numeric"
                              value={form.fleet_assets?.overview?.[key] || ""}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  fleet_assets: {
                                    ...(p.fleet_assets || {}),
                                    overview: {
                                      ...(p.fleet_assets?.overview || {}),
                                      [key]: String(e.target.value ?? "").replace(/[^\d]/g, ""),
                                    },
                                  },
                                }))
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-subsection">
                      <div className="lp-subsection-title">Containers</div>
                      <div className="lp-metrics-grid">
                        {[
                          ["containersTotal", "Total Containers"],
                          ["containers20ft", "20ft"],
                          ["containers40ft", "40ft"],
                          ["containersReefer", "Reefer"],
                        ].map(([key, label]) => (
                          <div key={key} className="lp-metric">
                            <div className="lp-metric-label">{label}</div>
                            <input
                              className="form-control lp-input-pill"
                              type="number"
                              min={0}
                              step={1}
                              inputMode="numeric"
                              value={form.fleet_assets?.overview?.[key] || ""}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  fleet_assets: {
                                    ...(p.fleet_assets || {}),
                                    overview: {
                                      ...(p.fleet_assets?.overview || {}),
                                      [key]: String(e.target.value ?? "").replace(/[^\d]/g, ""),
                                    },
                                  },
                                }))
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-subsection">
                      <div className="lp-subsection-title">Rail Wagons</div>
                      <div className="lp-metrics-grid">
                        {[
                          ["railWagonsTotal", "Total Rail Wagons"],
                          ["railCovered", "Covered"],
                          ["railOpen", "Open"],
                          ["railSpecial", "Special"],
                        ].map(([key, label]) => (
                          <div key={key} className="lp-metric">
                            <div className="lp-metric-label">{label}</div>
                            <input
                              className="form-control lp-input-pill"
                              type="number"
                              min={0}
                              step={1}
                              inputMode="numeric"
                              value={form.fleet_assets?.overview?.[key] || ""}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  fleet_assets: {
                                    ...(p.fleet_assets || {}),
                                    overview: {
                                      ...(p.fleet_assets?.overview || {}),
                                      [key]: String(e.target.value ?? "").replace(/[^\d]/g, ""),
                                    },
                                  },
                                }))
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-subsection">
                      <div className="lp-subsection-title">Warehouses &amp; Storage</div>
                      <div className="lp-metrics-grid">
                        <div className="lp-metric">
                          <div className="lp-metric-label">Total Warehouses</div>
                          <input
                            className="form-control lp-input-pill"
                            type="number"
                            min={0}
                            step={1}
                            inputMode="numeric"
                            value={form.fleet_assets?.warehousesStorage?.totalWarehouses || ""}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                fleet_assets: {
                                  ...(p.fleet_assets || {}),
                                  warehousesStorage: {
                                    ...(p.fleet_assets?.warehousesStorage || {}),
                                    totalWarehouses: String(e.target.value ?? "").replace(/[^\d]/g, ""),
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="lp-metric">
                          <div className="lp-metric-label">Total Area (sq ft)</div>
                          <input
                            className="form-control lp-input-pill"
                            type="number"
                            min={0}
                            step={1}
                            inputMode="numeric"
                            value={form.fleet_assets?.warehousesStorage?.totalAreaSqFt || ""}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                fleet_assets: {
                                  ...(p.fleet_assets || {}),
                                  warehousesStorage: {
                                    ...(p.fleet_assets?.warehousesStorage || {}),
                                    totalAreaSqFt: String(e.target.value ?? "").replace(/[^\d]/g, ""),
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="lp-metric">
                          <div className="lp-metric-label">Cold Storage Units</div>
                          <input
                            className="form-control lp-input-pill"
                            type="number"
                            min={0}
                            step={1}
                            inputMode="numeric"
                            value={form.fleet_assets?.warehousesStorage?.coldStorageUnits || ""}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                fleet_assets: {
                                  ...(p.fleet_assets || {}),
                                  warehousesStorage: {
                                    ...(p.fleet_assets?.warehousesStorage || {}),
                                    coldStorageUnits: String(e.target.value ?? "").replace(/[^\d]/g, ""),
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="lp-metric">
                          <div className="lp-metric-label">Bonded Warehouses</div>
                          <input
                            className="form-control lp-input-pill"
                            type="number"
                            min={0}
                            step={1}
                            inputMode="numeric"
                            value={form.fleet_assets?.warehousesStorage?.bondedWarehouses || ""}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                fleet_assets: {
                                  ...(p.fleet_assets || {}),
                                  warehousesStorage: {
                                    ...(p.fleet_assets?.warehousesStorage || {}),
                                    bondedWarehouses: String(e.target.value ?? "").replace(/[^\d]/g, ""),
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-subsection">
                      <div className="lp-subsection-title">Road Fleet Breakdown</div>
                      <div className="lp-metrics-grid">
                        {[
                          ["containerTrucks", "Container Trucks"],
                          ["openBodyTrucks", "Open Body Trucks"],
                          ["refrigeratedTrucks", "Refrigerated Trucks"],
                          ["tankers", "Tankers"],
                          ["flatbedTrailers", "Flatbed Trailers"],
                          ["lowBedTrailers", "Low Bed Trailers"],
                          ["multiAxleVehicles", "Multi-Axle Vehicles"],
                          ["lightCommercial", "Light Commercial"],
                        ].map(([key, label]) => (
                          <div key={key} className="lp-metric">
                            <div className="lp-metric-label">{label}</div>
                            <input
                              className="form-control lp-input-pill"
                              type="number"
                              min={0}
                              step={1}
                              inputMode="numeric"
                              value={form.fleet_assets?.roadFleetBreakdown?.[key] || ""}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  fleet_assets: {
                                    ...(p.fleet_assets || {}),
                                    roadFleetBreakdown: {
                                      ...(p.fleet_assets?.roadFleetBreakdown || {}),
                                      [key]: String(e.target.value ?? "").replace(/[^\d]/g, ""),
                                    },
                                  },
                                }))
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-subsection">
                      <div className="lp-subsection-title">Technology &amp; Tracking Capabilities</div>
                      <div className="lp-ship-grid">
                        <label className="lp-ship-item">
                          <input
                            type="checkbox"
                            checked={!!form.fleet_assets?.technologyTracking?.gpsFleetTracking}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                fleet_assets: {
                                  ...(p.fleet_assets || {}),
                                  technologyTracking: {
                                    ...(p.fleet_assets?.technologyTracking || {}),
                                    gpsFleetTracking: e.target.checked,
                                  },
                                },
                              }))
                            }
                          />
                          <span>GPS Fleet Tracking</span>
                        </label>
                        <label className="lp-ship-item">
                          <input
                            type="checkbox"
                            checked={!!form.fleet_assets?.technologyTracking?.iotSensors}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                fleet_assets: {
                                  ...(p.fleet_assets || {}),
                                  technologyTracking: {
                                    ...(p.fleet_assets?.technologyTracking || {}),
                                    iotSensors: e.target.checked,
                                  },
                                },
                              }))
                            }
                          />
                          <span>IoT Sensors</span>
                        </label>
                        <label className="lp-ship-item">
                          <input
                            type="checkbox"
                            checked={!!form.fleet_assets?.technologyTracking?.fleetManagementSystem}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                fleet_assets: {
                                  ...(p.fleet_assets || {}),
                                  technologyTracking: {
                                    ...(p.fleet_assets?.technologyTracking || {}),
                                    fleetManagementSystem: e.target.checked,
                                  },
                                },
                              }))
                            }
                          />
                          <span>Fleet Management System</span>
                        </label>
                      </div>

                      <div className="lp-metrics-grid lp-metrics-grid-3 mt-2">
                        {!!form.fleet_assets?.technologyTracking?.gpsFleetTracking && (
                          <div className="lp-metric">
                            <div className="lp-metric-label">GPS Coverage (%)</div>
                            <input
                              className="form-control lp-input-pill"
                              placeholder="e.g., 100"
                              type="number"
                              min={0}
                              max={100}
                              step={1}
                              inputMode="numeric"
                              value={form.fleet_assets?.technologyTracking?.gpsCoveragePercent || ""}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  fleet_assets: {
                                    ...(p.fleet_assets || {}),
                                    technologyTracking: {
                                      ...(p.fleet_assets?.technologyTracking || {}),
                                      gpsCoveragePercent: String(e.target.value ?? "").replace(/[^\d]/g, ""),
                                    },
                                  },
                                }))
                              }
                            />
                          </div>
                        )}
                        {!!form.fleet_assets?.technologyTracking?.iotSensors && (
                          <div className="lp-metric">
                            <div className="lp-metric-label">IoT Note / Tag</div>
                            <input
                              className="form-control lp-input-pill"
                              placeholder="e.g., Reefer Fleet"
                              value={form.fleet_assets?.technologyTracking?.iotTag || ""}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  fleet_assets: {
                                    ...(p.fleet_assets || {}),
                                    technologyTracking: {
                                      ...(p.fleet_assets?.technologyTracking || {}),
                                      iotTag: e.target.value,
                                    },
                                  },
                                }))
                              }
                            />
                          </div>
                        )}
                        {!!form.fleet_assets?.technologyTracking?.fleetManagementSystem && (
                          <div className="lp-metric">
                            <div className="lp-metric-label">FMS Note / Tag</div>
                            <input
                              className="form-control lp-input-pill"
                              placeholder="e.g., AI Powered"
                              value={form.fleet_assets?.technologyTracking?.fmsTag || ""}
                              onChange={(e) =>
                                setForm((p) => ({
                                  ...p,
                                  fleet_assets: {
                                    ...(p.fleet_assets || {}),
                                    technologyTracking: {
                                      ...(p.fleet_assets?.technologyTracking || {}),
                                      fmsTag: e.target.value,
                                    },
                                  },
                                }))
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showSection("coverage") && (
              <div className="lp-form">
                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-tab-title">Geographic Coverage &amp; Routes</div>
                    <div className="lp-tab-subtitle">Countries, cities, ports, and routes you serve</div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">Geographic Coverage (Countries/Regions)</label>
                    <div className="lp-addline">
                      <input
                        className="form-control lp-input-pill"
                        placeholder="e.g., India, UAE, USA"
                        value={coverageInput}
                        onChange={(e) => setCoverageInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const val = coverageInput.trim();
                            if (!val) return;
                            setCoverageInput("");
                            toggleNestedArrayValue("coverage_routes", "geographicCoverage", val);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="lp-addbtn"
                        onClick={() => {
                          const val = coverageInput.trim();
                          if (!val) return;
                          setCoverageInput("");
                          toggleNestedArrayValue("coverage_routes", "geographicCoverage", val);
                        }}
                        aria-label="Add geographic coverage"
                      >
                        +
                      </button>
                    </div>
                    <div className="lp-chiprow">
                      {(form.coverage_routes?.geographicCoverage || []).map((x) => (
                        <button
                          key={x}
                          type="button"
                          className="lp-chip"
                          onClick={() => toggleNestedArrayValue("coverage_routes", "geographicCoverage", x)}
                          title="Remove"
                        >
                          {x} <span aria-hidden="true">×</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">Ports &amp; Maritime Terminals</label>
                    <div className="lp-muted mb-2">Add port name, type, and traffic level</div>
                    <div className="lp-row lp-row-2">
                      <div className="lp-field">
                        <input
                          className="form-control lp-input-pill"
                          placeholder="Port name (e.g., JNPT - Mumbai)"
                          value={portDraft.name}
                          onChange={(e) => setPortDraft((p) => ({ ...p, name: e.target.value }))}
                        />
                      </div>
                      <div className="lp-field">
                        <select
                          className="form-control lp-input-pill"
                          value={portDraft.portType}
                          onChange={(e) => setPortDraft((p) => ({ ...p, portType: e.target.value }))}
                        >
                          <option value="">Select port type</option>
                          <option value="Major Port">Major Port</option>
                          <option value="Private Port">Private Port</option>
                          <option value="Container Terminal">Container Terminal</option>
                        </select>
                      </div>
                      <div className="lp-field">
                        <select
                          className="form-control lp-input-pill"
                          value={portDraft.traffic}
                          onChange={(e) => setPortDraft((p) => ({ ...p, traffic: e.target.value }))}
                        >
                          <option value="">Select traffic</option>
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm mt-2"
                      onClick={() => {
                        const name = String(portDraft.name || "").trim();
                        const portType = String(portDraft.portType || "").trim();
                        const traffic = String(portDraft.traffic || "").trim();
                        if (!name) return;
                        setForm((p) => {
                          const cur = Array.isArray(p.coverage_routes?.portsTerminals)
                            ? p.coverage_routes.portsTerminals
                            : [];
                          return {
                            ...p,
                            coverage_routes: {
                              ...(p.coverage_routes || {}),
                              portsTerminals: [...cur, { name, portType, traffic }],
                            },
                          };
                        });
                        setPortDraft({ name: "", portType: "", traffic: "" });
                      }}
                    >
                      Add port
                    </button>

                    {(form.coverage_routes?.portsTerminals || []).length ? (
                      <div className="mt-3">
                        {(form.coverage_routes.portsTerminals || []).map((p, idx) => (
                          <div key={`${p?.name || "port"}-${idx}`} className="lp-cert-row">
                            <div className="lp-cert-name">
                              <strong>{p?.name || "Port"}</strong>
                              <div className="small text-muted">
                                {[p?.portType, p?.traffic ? `Traffic: ${p.traffic}` : ""].filter(Boolean).join(" · ")}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn btn-link text-danger p-0"
                              onClick={() =>
                                setForm((prev) => {
                                  const cur = Array.isArray(prev.coverage_routes?.portsTerminals)
                                    ? prev.coverage_routes.portsTerminals
                                    : [];
                                  return {
                                    ...prev,
                                    coverage_routes: {
                                      ...(prev.coverage_routes || {}),
                                      portsTerminals: cur.filter((_, i) => i !== idx),
                                    },
                                  };
                                })
                              }
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted small mt-2">No ports/terminals added</div>
                    )}
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">Airport Network</label>
                    <div className="lp-addline">
                      <input
                        className="form-control lp-input-pill"
                        placeholder="e.g., Mumbai (BOM), Delhi (DEL)"
                        value={airportInput}
                        onChange={(e) => setAirportInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const val = airportInput.trim();
                            if (!val) return;
                            setAirportInput("");
                            toggleNestedArrayValue("coverage_routes", "airportNetwork", val);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="lp-addbtn"
                        onClick={() => {
                          const val = airportInput.trim();
                          if (!val) return;
                          setAirportInput("");
                          toggleNestedArrayValue("coverage_routes", "airportNetwork", val);
                        }}
                        aria-label="Add airport"
                      >
                        +
                      </button>
                    </div>
                    <div className="lp-chiprow">
                      {(form.coverage_routes?.airportNetwork || []).map((x) => (
                        <button
                          key={x}
                          type="button"
                          className="lp-chip"
                          onClick={() => toggleNestedArrayValue("coverage_routes", "airportNetwork", x)}
                          title="Remove"
                        >
                          {x} <span aria-hidden="true">×</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">Major Trade Lanes &amp; Corridors</label>
                    <div className="lp-muted mb-2">
                      Add lanes/corridors with transport modes, frequency, and traffic level
                    </div>

                    <div className="lp-row lp-row-4">
                      <div className="lp-field">
                        <input
                          className="form-control lp-input-pill"
                          placeholder="Lane/Corridor name (e.g., India - Middle East)"
                          value={corridorDraft.name}
                          onChange={(e) => setCorridorDraft((p) => ({ ...p, name: e.target.value }))}
                        />
                      </div>

                      <div className="lp-field">
                        <select
                          className="form-control lp-input-pill"
                          value={corridorDraft.frequency}
                          onChange={(e) => setCorridorDraft((p) => ({ ...p, frequency: e.target.value }))}
                        >
                          <option value="">Select frequency</option>
                          <option value="Daily">Daily</option>
                          <option value="Weekly">Weekly</option>
                          <option value="Monthly">Monthly</option>
                          <option value="Multiple Daily">Multiple Daily</option>
                          <option value="3x/week">3x/week</option>
                        </select>
                      </div>

                      <div className="lp-field">
                        <select
                          className="form-control lp-input-pill"
                          value={corridorDraft.traffic}
                          onChange={(e) => setCorridorDraft((p) => ({ ...p, traffic: e.target.value }))}
                        >
                          <option value="">Select traffic</option>
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                          <option value="Very High">Very High</option>
                        </select>
                      </div>

                      <div className="lp-field">
                        <select
                          className="form-control lp-input-pill"
                          value={(Array.isArray(corridorDraft.modes) ? corridorDraft.modes[0] : "") || ""}
                          onChange={(e) => {
                            const v = String(e.target.value || "");
                            setCorridorDraft((p) => ({ ...p, modes: v ? [v] : [] }));
                          }}
                        >
                          <option value="">Select mode</option>
                          <option value="air">Air</option>
                          <option value="ocean">Ocean</option>
                          <option value="road">Road</option>
                          <option value="rail">Rail</option>
                          <option value="multimodal">Multi-Modal</option>
                        </select>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm mt-2"
                      onClick={() => {
                        const name = String(corridorDraft.name || "").trim();
                        if (!name) return;
                        const modes = Array.isArray(corridorDraft.modes) ? corridorDraft.modes : [];
                        const frequency = String(corridorDraft.frequency || "").trim();
                        const traffic = String(corridorDraft.traffic || "").trim();
                        setForm((p) => {
                          const cur = Array.isArray(p.coverage_routes?.corridors) ? p.coverage_routes.corridors : [];
                          return {
                            ...p,
                            coverage_routes: {
                              ...(p.coverage_routes || {}),
                              corridors: [...cur, { name, modes, frequency, traffic }],
                            },
                          };
                        });
                        setCorridorDraft({ name: "", modes: [], frequency: "", traffic: "" });
                      }}
                    >
                      Add lane/corridor
                    </button>

                    {(form.coverage_routes?.corridors || []).length ? (
                      <div className="mt-3">
                        {(form.coverage_routes.corridors || []).map((c, idx) => (
                          <div key={`${c?.name || "corridor"}-${idx}`} className="lp-cert-row">
                            <div className="lp-cert-name">
                              <strong>{c?.name || "Lane"}</strong>
                              <div className="lp-chiprow lp-chiprow-split">
                                <div className="lp-chipgroup">
                                  {(Array.isArray(c?.modes) ? c.modes : [])
                                    .map((m) => {
                                      const s = String(m || "").toLowerCase();
                                      if (s === "ocean") return "Ocean";
                                      if (s === "air") return "Air";
                                      if (s === "road") return "Road";
                                      if (s === "rail") return "Rail";
                                      if (s === "multimodal") return "Multi-Modal";
                                      return s ? s[0].toUpperCase() + s.slice(1) : "";
                                    })
                                    .filter(Boolean)
                                    .map((label, i) => (
                                      <div key={`${label}-${i}`} className="lp-chip">
                                        {label}
                                      </div>
                                    ))}
                                </div>

                                {c?.traffic ? (
                                  <div className="lp-chip">
                                    Traffic <span>{c.traffic}</span>
                                  </div>
                                ) : null}
                              </div>

                              {c?.frequency ? (
                                <div className="small text-muted mt-1">Frequency: {c.frequency}</div>
                              ) : null}
                            </div>
                            <button
                              type="button"
                              className="btn btn-link text-danger p-0"
                              onClick={() =>
                                setForm((prev) => {
                                  const cur = Array.isArray(prev.coverage_routes?.corridors)
                                    ? prev.coverage_routes.corridors
                                    : [];
                                  return {
                                    ...prev,
                                    coverage_routes: {
                                      ...(prev.coverage_routes || {}),
                                      corridors: cur.filter((_, i) => i !== idx),
                                    },
                                  };
                                })
                              }
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted small mt-2">No lanes/corridors added</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {showSection("certifications") && (
              <div className="lp-section-block lp-section-block--certifications">
              <div className="lp-form">
                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-tab-title">Certifications &amp; Compliance</div>
                    <div className="lp-tab-subtitle">Industry certifications, registrations, and legal documents</div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-subsection-title">Company Registrations</div>
                    <div className="lp-metrics-grid lp-metrics-grid-3">
                      <div className="lp-metric">
                        <div className="lp-metric-label">CIN Number</div>
                        <input
                          className="form-control lp-input-pill"
                          placeholder="e.g., U63040MH2009PTC192456"
                          value={form.certifications?.registrations?.cinNumber || ""}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              certifications: {
                                ...(p.certifications || {}),
                                registrations: {
                                  ...(p.certifications?.registrations || {}),
                                  cinNumber: e.target.value,
                                },
                              },
                            }))
                          }
                        />
                      </div>
                      <div className="lp-metric">
                        <div className="lp-metric-label">GSTIN</div>
                        <input
                          className="form-control lp-input-pill"
                          placeholder="e.g., 27AABCU9603R1ZV"
                          value={form.certifications?.registrations?.gstin || ""}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              certifications: {
                                ...(p.certifications || {}),
                                registrations: {
                                  ...(p.certifications?.registrations || {}),
                                  gstin: e.target.value,
                                },
                              },
                            }))
                          }
                        />
                      </div>
                      <div className="lp-metric">
                        <div className="lp-metric-label">PAN</div>
                        <input
                          className="form-control lp-input-pill"
                          placeholder="e.g., AABCU9603R"
                          value={form.certifications?.registrations?.pan || ""}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              certifications: {
                                ...(p.certifications || {}),
                                registrations: {
                                  ...(p.certifications?.registrations || {}),
                                  pan: e.target.value,
                                },
                              },
                            }))
                          }
                        />
                      </div>
                      <div className="lp-metric">
                        <div className="lp-metric-label">Import Export Code (IEC)</div>
                        <input
                          className="form-control lp-input-pill"
                          placeholder="e.g., 0509012345"
                          value={form.certifications?.registrations?.iec || ""}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              certifications: {
                                ...(p.certifications || {}),
                                registrations: {
                                  ...(p.certifications?.registrations || {}),
                                  iec: e.target.value,
                                },
                              },
                            }))
                          }
                        />
                      </div>
                      <div className="lp-metric">
                        <div className="lp-metric-label">MSME Registration</div>
                        <input
                          className="form-control lp-input-pill"
                          placeholder="e.g., UDYAM-MH-00-0123456"
                          value={form.certifications?.registrations?.msme || ""}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              certifications: {
                                ...(p.certifications || {}),
                                registrations: {
                                  ...(p.certifications?.registrations || {}),
                                  msme: e.target.value,
                                },
                              },
                            }))
                          }
                        />
                      </div>
                      <div className="lp-metric">
                        <div className="lp-metric-label">TAN Number</div>
                        <input
                          className="form-control lp-input-pill"
                          placeholder="e.g., MUMA12345B"
                          value={form.certifications?.registrations?.tan || ""}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              certifications: {
                                ...(p.certifications || {}),
                                registrations: {
                                  ...(p.certifications?.registrations || {}),
                                  tan: e.target.value,
                                },
                              },
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-cert-header">
                      <div className="lp-subsection-title" style={{ marginBottom: 0 }}>Industry Certifications</div>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setCertModalOpen(true)}
                      >
                        + Add More
                      </button>
                    </div>

                    {(form.certifications?.industryCertifications || []).length === 0 ? (
                      <div className="lp-emptybox">
                        <div className="lp-empty-icon">🏅</div>
                        <div className="lp-empty-title">No certifications added yet</div>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => setCertModalOpen(true)}
                        >
                          + Add First Certification
                        </button>
                      </div>
                    ) : (
                      <div className="lp-cert-list">
                        {(form.certifications?.industryCertifications || []).map((c, idx) => (
                          <div key={`${c.name}-${idx}`} className="lp-cert-item">
                            <div className="lp-cert-main">
                              <div className="lp-cert-name">{c.name || "Certification"}</div>
                              <div className="lp-cert-meta">
                                {[c.issuer, c.issuedDate, c.validity].filter(Boolean).join(" • ")}
                              </div>
                            </div>
                            <div className="lp-cert-actions">
                              {c.attachmentUrl ? (
                                <a
                                  className="btn btn-outline-secondary btn-sm"
                                  href={resolvePublicFileUrl(c.attachmentUrl)}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View
                                </a>
                              ) : null}
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => {
                                  setForm((p) => ({
                                    ...p,
                                    certifications: {
                                      ...(p.certifications || {}),
                                      industryCertifications: (p.certifications?.industryCertifications || []).filter(
                                        (_, i) => i !== idx
                                      ),
                                    },
                                  }));
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {certModalOpen && (
                  <div className="lp-modal-backdrop" role="dialog" aria-modal="true">
                    <div className="lp-modal">
                      <div className="lp-modal-title">Add Certification</div>
                      <div className="lp-modal-grid">
                        <div className="lp-field">
                          <label className="lp-label">Certification Name</label>
                          <input
                            className="form-control lp-input-pill"
                            value={certDraft.name}
                            onChange={(e) => setCertDraft((p) => ({ ...p, name: e.target.value }))}
                          />
                        </div>
                        <div className="lp-field">
                          <label className="lp-label">Issuer</label>
                          <input
                            className="form-control lp-input-pill"
                            value={certDraft.issuer}
                            onChange={(e) => setCertDraft((p) => ({ ...p, issuer: e.target.value }))}
                          />
                        </div>
                        <div className="lp-field">
                          <label className="lp-label">Issued Date</label>
                          <input
                            type="date"
                            className="form-control lp-input-pill"
                            value={certDraft.issuedDate}
                            onChange={(e) => setCertDraft((p) => ({ ...p, issuedDate: e.target.value }))}
                          />
                        </div>
                        <div className="lp-field">
                          <label className="lp-label">Validity / Expiry</label>
                          <input
                            type="date"
                            className="form-control lp-input-pill"
                            value={certDraft.validity}
                            onChange={(e) => setCertDraft((p) => ({ ...p, validity: e.target.value }))}
                          />
                        </div>
                        <div className="lp-field" style={{ gridColumn: "1 / -1" }}>
                          <label className="lp-label">Attachment</label>
                          <div className="d-flex gap-2 align-items-center">
                            <input
                              ref={certFileRef}
                              type="file"
                              className="d-none"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  setLoading(true);
                                  const path = await uploadLogo(file);
                                  setCertDraft((p) => ({ ...p, attachmentUrl: path }));
                                  toast.success("Attachment uploaded");
                                } catch (err) {
                                  toast.error(err?.message || "Failed to upload attachment");
                                } finally {
                                  setLoading(false);
                                  if (certFileRef.current) certFileRef.current.value = "";
                                }
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => certFileRef.current?.click()}
                              disabled={loading}
                            >
                              Upload
                            </button>
                            {certDraft.attachmentUrl ? (
                              <a
                                className="btn btn-outline-secondary btn-sm"
                                href={resolvePublicFileUrl(certDraft.attachmentUrl)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View
                              </a>
                            ) : (
                              <span className="lp-help">Optional PDF/JPG/PNG</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="lp-modal-actions">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            setCertModalOpen(false);
                            setCertDraft({ name: "", issuer: "", issuedDate: "", validity: "", attachmentUrl: "" });
                          }}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => {
                            const name = certDraft.name.trim();
                            if (!name) {
                              toast.error("Certification Name is required");
                              return;
                            }
                            setForm((p) => ({
                              ...p,
                              certifications: {
                                ...(p.certifications || {}),
                                industryCertifications: [
                                  ...(p.certifications?.industryCertifications || []),
                                  { ...certDraft, name },
                                ],
                              },
                            }));
                            setCertModalOpen(false);
                            setCertDraft({ name: "", issuer: "", issuedDate: "", validity: "", attachmentUrl: "" });
                          }}
                          disabled={loading}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              </div>
            )}

            {showSection("commercial") && (
              <div className="lp-form">
                <div className="lp-row">
                  <div className="lp-field">
                    <div className="lp-tab-title">Commercial Terms &amp; Pricing</div>
                    <div className="lp-tab-subtitle">Payment terms, pricing models, and commercial preferences</div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">Pricing Models Offered</label>
                    <div className="lp-two-col-checks">
                      {[
                        "Spot Quote Pricing",
                        "Contract Pricing",
                        "Fixed Rate Contracts",
                        "Dedicated Fleet Pricing",
                      ].map((x) => {
                        const checked = (form.capabilities?.commercialTerms?.pricingModelsOffered || []).includes(x);
                        return (
                          <label key={x} className="lp-checkline">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                setForm((p) => {
                                  const cur = p.capabilities?.commercialTerms?.pricingModelsOffered || [];
                                  const next = cur.includes(x) ? cur.filter((v) => v !== x) : [...cur, x];
                                  return {
                                    ...p,
                                    capabilities: {
                                      ...(p.capabilities || {}),
                                      commercialTerms: {
                                        ...(p.capabilities?.commercialTerms || {}),
                                        pricingModelsOffered: next,
                                      },
                                    },
                                  };
                                });
                              }}
                            />
                            <span>{x}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row lp-row-commercial-terms">
                  <div className="lp-field">
                    <label className="lp-label">Payment Terms</label>
                    <Select
                      isMulti
                      className="lp-lang-select"
                      classNamePrefix="lp-lang-rs"
                      placeholder="Select payment terms"
                      options={paymentTermSelectOptions}
                      value={paymentTermsSelectValue}
                      onChange={(opts) =>
                        setForm((p) => ({
                          ...p,
                          capabilities: {
                            ...(p.capabilities || {}),
                            commercialTerms: {
                              ...(p.capabilities?.commercialTerms || {}),
                              paymentTerms: (opts || []).map((o) => o.value),
                            },
                          },
                        }))
                      }
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      isClearable
                      noOptionsMessage={() => "No payment terms loaded"}
                    />
                  </div>
                  <div className="lp-field">
                    <label className="lp-label">Credit Period (days)</label>
                    <input
                      className="form-control lp-input-pill"
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      placeholder="e.g., 30"
                      value={form.capabilities?.commercialTerms?.creditPeriodDays || ""}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          capabilities: {
                            ...(p.capabilities || {}),
                            commercialTerms: {
                              ...(p.capabilities?.commercialTerms || {}),
                              creditPeriodDays: String(e.target.value ?? "").replace(/[^\d]/g, ""),
                            },
                          },
                        }))
                      }
                    />
                  </div>
                  <div className="lp-field">
                    <label className="lp-label">Currencies Accepted</label>
                    <Select
                      isMulti
                      className="lp-lang-select"
                      classNamePrefix="lp-lang-rs"
                      placeholder="Select currencies accepted"
                      options={currencySelectOptions}
                      value={currenciesAcceptedSelectValue}
                      onChange={(opts) =>
                        setForm((p) => ({
                          ...p,
                          capabilities: {
                            ...(p.capabilities || {}),
                            commercialTerms: {
                              ...(p.capabilities?.commercialTerms || {}),
                              currenciesAccepted: (opts || []).map((o) => o.value),
                            },
                          },
                        }))
                      }
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      isClearable
                      noOptionsMessage={() => "No currencies loaded"}
                    />
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field" style={{ gridColumn: "1 / -1" }}>
                    <label className="lp-label">Credit period (display)</label>
                    <input
                      className="form-control lp-input-pill"
                      placeholder='e.g., "30-45 days" (optional — overrides "Credit Period (days)" on the public profile)'
                      value={form.capabilities?.commercialTerms?.creditPeriodLabel || ""}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          capabilities: {
                            ...(p.capabilities || {}),
                            commercialTerms: {
                              ...(p.capabilities?.commercialTerms || {}),
                              creditPeriodLabel: e.target.value,
                            },
                          },
                        }))
                      }
                    />
                    <div className="lp-muted small mt-1">
                      If filled, this text is shown as <strong>Credit Period</strong> on the Performance tab. Otherwise we derive it
                      from <strong>Credit Period (days)</strong>.
                    </div>
                  </div>
                </div>

                <div className="lp-divider" />

                <div className="lp-row">
                  <div className="lp-field">
                    <label className="lp-label">Commercial support</label>
                    <textarea
                      className="form-control lp-input-pill"
                      rows={5}
                      placeholder={"One bullet per line\ne.g., Dedicated Account Manager"}
                      value={(form.capabilities?.commercialTerms?.commercialSupport || []).join("\n")}
                      onChange={(e) => {
                        const lines = String(e.target.value || "")
                          .split(/\r?\n/g)
                          .map((s) => s.trim())
                          .filter(Boolean);
                        setForm((p) => ({
                          ...p,
                          capabilities: {
                            ...(p.capabilities || {}),
                            commercialTerms: {
                              ...(p.capabilities?.commercialTerms || {}),
                              commercialSupport: lines,
                            },
                          },
                        }));
                      }}
                    />
                  </div>
                  <div className="lp-field">
                    <label className="lp-label">Minimum requirements</label>
                    <textarea
                      className="form-control lp-input-pill"
                      rows={5}
                      placeholder={"One bullet per line\ne.g., No Minimum Shipment Value"}
                      value={(form.capabilities?.commercialTerms?.minimumRequirements || []).join("\n")}
                      onChange={(e) => {
                        const lines = String(e.target.value || "")
                          .split(/\r?\n/g)
                          .map((s) => s.trim())
                          .filter(Boolean);
                        setForm((p) => ({
                          ...p,
                          capabilities: {
                            ...(p.capabilities || {}),
                            commercialTerms: {
                              ...(p.capabilities?.commercialTerms || {}),
                              minimumRequirements: lines,
                            },
                          },
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="lp-section-nav lp-section-nav--edit">
              <span className="lp-section-nav__step">
                Step {sectionIndex + 1} of {SECTIONS.length}
              </span>
              <div className="lp-section-nav__actions">
                <button
                  type="button"
                  className="btn btn-outline-secondary lp-section-nav-btn"
                  onClick={goPrevSection}
                  disabled={sectionIndex <= 0}
                >
                  ← Back
                </button>
                <button type="button" className="btn btn-outline-secondary lp-section-nav-btn" onClick={handleSave} disabled={loading}>
                  {loading ? "Saving..." : "Save Profile"}
                </button>
                <button
                  type="button"
                  className="btn btn-primary lp-section-nav-btn"
                  onClick={goNextSection}
                  disabled={sectionIndex >= SECTIONS.length - 1}
                >
                  Save &amp; Next →
                </button>
              </div>
            </div>

            {!companyId && (
              <div className="lp-footer-warning">
                <strong>Note:</strong> This profile is linked to your Trade company profile. Please complete your Trade
                Profile first to enable saving the Logistics profile.
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

